// ─────────────────────────────────────────────────────────────────────────────
// Changelog content — edit this file to log a deployment.
//
// Add a new object to the TOP of the array each time you deploy. Newest first.
//
// Release shape:
//   {
//     date: "2026-06-10T15:00:00",   // deployment day + time, LOCAL ISO (no Z)
//     version: "v2.14.0",            // optional label/tag shown as a mono chip
//     title: "Headline for the release",   // optional bold headline
//     summary: "One-line description of the release.",   // optional sub-line
//     deployNote: "no downtime",     // optional extra on the "Deployed …" line
//     hidden: true,                  // optional — keep it off the Changelog page
//                                    //   (the Admin Deployment Review still shows it)
//     roles: ["admin"],              // optional — hide the WHOLE release unless
//                                    //   the viewer matches a listed role
//     highlights: [                  // optional — the What's new pop-up's rows
//       { type: "feature", title: "Few words", summary: "One short sentence." },
//     ],                             //   (max 5). Keep them VERY high level.
//                                    //   Without them the pop-up lists feature
//                                    //   labels, or rolls up by section when a
//                                    //   release has more than 5.
//     changes: [ <change>, ... ],
//   }
//
// The newest release (top of the array) is automatically tagged "Latest".
//
// ORDER OF CHANGES (always): within every release's `changes` array, sort by
// type in this order — "feature" (New) first, then "improvement" (Improved),
// then "fix" (Fixed). New > Improved > Fixed, always.
//
// Change shape:
//   {
//     type: "feature" | "improvement" | "fix",   // the top-level group
//     section: "Loan Drilldown",     // where it lives: portal section (level 2)
//     area: "Documents",             //   the screen / tab, no "Tab" suffix (level 3)
//     feature: "Bulk actions",       //   the control / panel — bold label above the bullet
//     title: "Short headline",       // fallback label for entries without `feature`
//     description: "One idea, in plain sentences.",   // the bullet text
//
// The page puts each `section` in its own card, groups by `area` inside it,
// and lists `feature` rows New → Improved → Fixed with a type pill. Rows
// sharing type + section + area + feature merge under one label. Entries with
// no `section` render under "Other updates".
//     links: [ { label: "Open it", url: "/loans/closing/calendar" } ],  // optional
//     images: [ { src: "/changelog-images/foo.png", alt: "…", caption: "…" } ], // optional
//     roles: ["capitalMarkets"],     // optional — hide THIS change unless the
//                                    //   viewer matches a listed role
//   }
//
// Notes:
//   • links — url may be internal ("/loans/...") or external ("https://…").
//   • images — src may be an external URL, or a file you drop in the app's
//     public/ folder, e.g. public/changelog-images/foo.png → src: "/changelog-images/foo.png".
//     (The folder is "changelog-images", NOT "changelog" — a "changelog" folder
//     would shadow the /changelog route and break a hard refresh on that page.)
//   • roles — omit for "everyone". Recognized values: "superAdmin", "admin",
//     "capitalMarkets", "closer", "underwriter". An admin or super-admin change
//     shows an "Admin" chip on the Changelog page (its hover says who sees it).
//     Anything sizer related or admin-permission related is ["admin"];
//     something only super admins can use is ["superAdmin"]. Super admins see
//     every change. A user sees the item if they match ANY listed
//     role. Put `roles` on a release to gate the whole entry, or on a single
//     change to gate just that line.
// ─────────────────────────────────────────────────────────────────────────────

export const CHANGELOG = [
  {
    date: "2026-11-03T18:00:00",
    version: "2026.11.03",
    title: "Note autosave and borrower class on the pipeline",
    summary:
      "Loan notes can save as you type, and the Loan Pipeline can show each loan's borrower class.",
    highlights: [
      {
        type: "feature",
        title: "Autosave for loan notes",
        summary: "Turn on Autosave in the Underwriting and Processing notes and they save a few seconds after you stop typing.",
      },
      {
        type: "feature",
        title: "Borrower class columns",
        summary: "Add Borrower Class and Calculated Class to the Loan Pipeline from the Columns menu, with overrides marked.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Notes",
        feature: "Autosave",
        title: "Autosave for loan notes",
        description:
          "Turn on Autosave in the Underwriting and Processing notes footer and the note saves a few seconds after you stop typing, and when you switch notes or close the panel.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Loan Pipeline",
        feature: "Borrower class columns",
        title: "Borrower class on the pipeline",
        description:
          "Add Borrower Class and Calculated Class from the Columns menu to sort and filter loans by class, with overrides marked.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Notes",
        feature: "Save",
        title: "Clearer note saving",
        description:
          "The notes footer shows Unsaved changes, Saving, Saved with how long ago, or Couldn't save with Retry, Ctrl+S saves, the note's tab gets a dot while it has unsaved text, and closing the browser tab with unsaved text asks first.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Side menu",
        feature: "Collapsed menu",
        title: "Larger collapsed menu labels",
        description:
          "The collapsed side menu is a little wider with larger, easier to read labels.",
      },
    ],
  },
  {
    date: "2026-11-02T18:00:00",
    version: "2026.11.02",
    reviewRevision: 2,
    title: "Closing costs and reserves on the sizers, correspondent required documents and agreements, and expired locks",
    summary:
      "The RTL and GUC Sizers handle financed closing costs and interest reserves, the Onboarding Checklist tracks required documents and several agreements, and an expired lock opens a new request.",
    highlights: [
      {
        type: "feature",
        title: "Closing costs and reserves on the sizers",
        summary: "Choose whether closing costs and the interest reserve are financed or paid at closing, and the loan amounts follow.",
      },
      {
        type: "feature",
        title: "Required documents on the checklist",
        summary: "See what each correspondent still needs and update documents right from the table.",
      },
      {
        type: "feature",
        title: "More than one agreement",
        summary: "A correspondent can carry MLPA, LFA and Broker agreements together.",
      },
      {
        type: "feature",
        title: "Expired locks",
        summary: "Request a new lock at today's pricing, or extend the old one.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Costs at closing",
        title: "Closing costs on the RTL Sizer",
        description:
          "Each property has Closing costs and Assignment fees, and once there are closing costs you choose whether they are financed into the loan or paid by the borrower at closing.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Interest reserve",
        title: "Interest reserve on the RTL Sizer",
        description:
          "Each property has an interest reserve of 1 to 12 months, or the months the guidelines require, financed or paid by the borrower, with the required and applied reserve shown after pricing.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "GUC Sizer",
        feature: "Property",
        title: "Closing costs and reserve on the GUC Sizer",
        description:
          "The GUC Sizer uses the same closing cost and interest reserve fields, including whether closing costs are financed, and a blank reserve now applies the required months.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Required documents",
        title: "Required documents on the checklist",
        description:
          "A Required documents section lists what each document list still needs and what is done, and clicking a document jumps to its row.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Documents",
        title: "Work documents in the table",
        description:
          "Change a document's status, upload or drop a file, or link a file already on the correspondent right from its row, each saved straight away.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Compliance",
        title: "Pick more than one agreement",
        description:
          "Compliance can hold more than one agreement, such as MLPA and LFA, with No agreement to clear them.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Profile",
        feature: "Legal Agreements",
        title: "Legal Agreements on the Profile",
        description:
          "The MLPA section is now Legal Agreements, with MLPA signed, LFA and Broker Agreement checkboxes that match the checklist's agreements.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Expired locks",
        title: "Request a new lock when one expires",
        description:
          "An expired lock now opens a new lock request at today's pricing, with a link to extend the expired lock instead.",
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Loan amounts",
        title: "Financed costs in the loan amounts",
        description:
          "Financed closing costs and a financed reserve are part of the initial loan amount, shown under it on both sizers, and a purchase with no closing costs entered uses an estimated 5% of the price.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Loan amounts",
        title: "Last priced leverage or amount",
        description:
          "The leverage and loan amount panel shows the other side of the last priced run.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "Liquidity broken down",
        description:
          "Required liquidity lists down payment, borrower paid closing costs and reserve and the other parts, and a financed reserve no longer counts as cash needed.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Progress",
        title: "Progress on every section",
        description:
          "Every section and its link on the side shows a check when complete, or a progress ring with how many items are done.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "NDA and questionnaire",
        title: "Simpler NDA and questionnaire",
        description:
          "The NDA is a single Sent toggle with its date, and uploading the questionnaire marks it sent and received.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Pricing",
        feature: "Counterparty terms",
        title: "Counterparty terms moved up",
        description:
          "Counterparty terms now sit right after DSCR pricing.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Correspondents",
        feature: "Underwriters",
        title: "Underwriter assignments removed",
        description:
          "The RTL and DSCR underwriter assignments and columns are gone, and the MLPA signatory is set on the Profile instead of Contacts.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "A two column Compliance layout with the Asset Management Agreement beside the agreements, and clearer column headings and hints.",
      },
      {
        type: "fix",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Reopened scenarios",
        title: "Reserves on reopened scenarios",
        description:
          "Fixed a reopened scenario keeping its old reserve amount, and pricing now uses whichever of leverage or loan amount you entered.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Expired locks",
        title: "Cancel on expired locks",
        description:
          "Fixed Cancel lock being offered on an expired lock, which could not be cancelled.",
      },
      {
        type: "fix",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Loan amounts",
        title: "Loan amounts right after pricing",
        description:
          "Fixed a loan sized by amount showing the initial loan amount as the total, without the holdback, right after pricing, and a construction budget no longer adds to the total on Stabilized Bridge and DSCR loans.",
        roles: ["admin"],
      },
    ],
  },
  {
    date: "2026-10-23T18:00:00",
    version: "2026.10.23",
    title: "Correspondent onboarding, Agreement Documents, loans and notes, and the Appraisal Queue correspondent column",
    summary:
      "Correspondents get a rebuilt Agreement Documents tab, a quicker Onboarding Checklist, both pipelines on Loans, safer notes and a new Admin tab, and the Appraisal Queue shows each loan's correspondent.",
    highlights: [
      {
        type: "feature",
        title: "Agreement Documents rebuilt",
        summary: "See every correspondent document by type, including what is still missing.",
      },
      {
        type: "feature",
        title: "Faster onboarding",
        summary: "Checkboxes that stamp the date, uploads right from the checklist, and green checks as sections finish.",
      },
      {
        type: "feature",
        title: "All of a correspondent's loans",
        summary: "Pre-close and acquisition loans together on the Loans tab.",
      },
      {
        type: "feature",
        title: "Safer notes",
        summary: "Drafts are kept, and any earlier version can be restored.",
      },
      {
        type: "feature",
        title: "Correspondent on the Appraisal Queue",
        summary: "Sort, filter and search the queue by correspondent.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Correspondents",
        area: "Admin",
        feature: "Admin tab",
        title: "Admin tab on correspondents",
        description:
          "A new Admin tab holds appraisal lender names, bank details, branding, email domains and volume estimates, moved off the Profile tab with their own Save.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Profile",
        feature: "Address",
        title: "Find an address on the Profile",
        description:
          "A Find an address search fills in the street, city, state, ZIP and county, and every part stays editable.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Profile",
        feature: "MLPA",
        title: "Upload the executed MLPA",
        description:
          "Upload the executed MLPA right from the Profile, which saves it as the final version and marks the MLPA signed.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Profile",
        feature: "MLPA",
        title: "MLPA signatory",
        description:
          "Set or edit the MLPA signatory in a contact panel without leaving the tab.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Prospect questionnaire",
        title: "Upload the questionnaire from the checklist",
        description:
          "Upload the prospect questionnaire on the checklist, which marks it received and adds it to Agreements.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Checkboxes",
        title: "Checkboxes that stamp the date",
        description:
          "NDA drafted and sent, and questionnaire sent and received, are checkboxes that stamp today's date, which you can change.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Progress",
        title: "Completed sections",
        description:
          "Each checklist section shows a green check as soon as everything it asks for is filled in.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Progress",
        title: "Status on section headers",
        description:
          "Initial engagement and Prospect questionnaire show where the NDA and questionnaire stand in their headers, even when folded.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Agreements",
        feature: "Agreement Documents",
        title: "Agreement Documents rebuilt",
        description:
          "Agreements is now Agreement Documents, with an Uploaded view grouped by type and an All Types view that shows what is missing, plus search and drag and drop upload.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Loans",
        feature: "Pipelines",
        title: "Pre-close and acquisition loans",
        description:
          "The Loans tab lists the correspondent's pre-close and acquisition loans, with a Pipeline column and a Closing / Settlement date.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Notes",
        feature: "Drafts",
        title: "Unsaved note drafts",
        description:
          "Unsaved note text is kept as a draft with a Discard button, closing the browser tab asks first, and you are warned if someone saved a newer version meanwhile.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Notes",
        feature: "Version history",
        title: "Note version history",
        description:
          "Open any earlier version of the note read only and restore it, with who saved it and when.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Contacts",
        feature: "Full name",
        title: "Full name on new contacts",
        description:
          "A new contact starts with one Full name box that splits into first and last name.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "Correspondent",
        title: "Correspondent on the Appraisal Queue",
        description:
          "Both Appraisal Queue tabs have a Correspondent column you can sort, filter and search by.",
        roles: ["underwriter", "admin"],
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Initial engagement",
        title: "Channel and client type",
        description:
          "Channel is fixed to Eastview, and Client type is a choice of Correspondent, Broker or No Type.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Onboarding Checklist",
        feature: "Compliance",
        title: "Compliance section",
        description:
          "The agreement is now one Agreement choice under Compliance, including No Agreement, and ticking Asset Management Agreement applies shows its sent and executed dates.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Profile",
        feature: "Relationship status",
        title: "Relationship status buttons",
        description:
          "Relationship status is a row of buttons, with the current one shown in its status color.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Pricing",
        feature: "RTL Buy Rate",
        title: "RTL Buy Rate",
        description:
          "RTL pricing tier is now called RTL Buy Rate.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Loans",
        feature: "Volume chart",
        title: "Loans chart and acquisition loans",
        description:
          "The volume chart plots by closing or settlement month, so acquisition loans count, and an acquisition row opens its acquisition page.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Correspondents",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Onboarding is now Onboarding Checklist, Notes shows a dot for an unsaved draft, the header pills are outlined, and the Loans tab and descriptions use the full width.",
      },
      {
        type: "improvement",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "External orders",
        title: "External orders",
        description:
          "Outside orders are now called External orders.",
        roles: ["underwriter", "admin"],
      },
      {
        type: "improvement",
        section: "Support",
        area: "Request Board",
        feature: "Request panel",
        title: "Recurring Encounters section",
        description:
          "Recurring Encounters is a collapsible section above Activity, with its count and Log encounter in the header, and the panel's sections are evenly spaced.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Correspondents",
        area: "Notes",
        feature: "Notes editor",
        title: "Notes opening blank",
        description:
          "Fixed a saved note sometimes opening blank, or not updating when you viewed an older version or loaded a newer one.",
      },
      {
        type: "fix",
        section: "Correspondents",
        area: "Notes",
        feature: "Save",
        title: "Save without changes",
        description:
          "Fixed Save turning on just from opening a note.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Email",
        feature: "Compose",
        title: "Email body opening blank",
        description:
          "Fixed a loaded template sometimes leaving the email body blank.",
      },
      {
        type: "fix",
        section: "General",
        area: "Forms",
        feature: "Address entry",
        title: "ZIP lookup on manual addresses",
        description:
          "Fixed every ZIP lookup failing when typing an address by hand, and the map pin now sits at the center of the ZIP code.",
      },
      {
        type: "fix",
        section: "General",
        area: "Forms",
        feature: "Address entry",
        title: "State on manual addresses",
        description:
          "Fixed the State dropdown not showing or selecting states when typing an address by hand. The hand typed address now sits in its own box, and State saves the short code.",
      },
    ],
  },
  {
    date: "2026-10-22T18:00:00",
    version: "2026.10.22",
    title: "Experience units and credits, settlement dates on acquisitions, and a cleaner Correspondents list",
    summary:
      "Experience items can count units sold individually and credit several parties from a new picker, acquisition loans show settlement dates, and the Correspondents list gains column filters and a view dropdown.",
    highlights: [
      {
        type: "feature",
        title: "Units sold individually",
        summary: "Experience items can count once per unit sold.",
      },
      {
        type: "feature",
        title: "New credits picker",
        summary: "Credit borrowers, entities and contractors from one checklist.",
      },
      {
        type: "improvement",
        title: "Settlement dates",
        summary: "Acquisition loans show settlement dates, not closing dates.",
      },
      {
        type: "improvement",
        title: "Correspondents list",
        summary: "Column filters, a view dropdown and full names.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Experience",
        feature: "Units sold individually",
        title: "Units sold individually on experience items",
        description:
          "Experience items have a Units sold individually field, so a property sold unit by unit, like a condo sell-out, counts once per unit, and the Qualifying category hint shows the math.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Experience",
        feature: "Notes",
        title: "Notes on experience items",
        description:
          "Experience items have a Notes field for why the item counts the way it does, with a warning when an item counts more than one unit and has no note.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Experience",
        feature: "Credits",
        title: "New credits picker on experience items",
        description:
          "Choosing who gets credit is now a checklist each for Borrowers, Entities and General contractors, with a detail line per party, an N of M selected count, Use suggested, Clear and Select all, Suggested tags and a warning when two parties share a name.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Experience",
        feature: "Experience items",
        title: "Units column on experience items",
        description:
          "The experience items list has a Units column, shown in amber with a warning when a multi-unit item has no note.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Request Board",
        feature: "Auth token",
        title: "Auth token on the Request Board",
        description:
          "Board developers have a key button beside Refresh that shows their sign-in token, with show or hide, copy and when it expires.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Experience",
        feature: "Credits",
        title: "Credit several parties at once",
        description:
          "The Credits section stays open after the first pick, so you can credit several parties in one go.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Experience",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Experience type, Resolution and Qualifying category are dropdowns in a two by two layout, and the credits checklist has initials, highlighted selections and tags. This applies on Experience Calc., Applicants and Contacts.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Acquisitions",
        feature: "Settlement dates",
        title: "Settlement dates on acquisition loans",
        description:
          "Acquisition loans show expected settlement and settlement dates instead of closing dates in Key Dates & Totals, Key Dates & Docs, the Summary and the Wire & Funding statement.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "List",
        feature: "Views",
        title: "List and Matrix views in a dropdown",
        description:
          "A dropdown beside the search box switches between Correspondent List View and Correspondent Matrix View.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "List",
        feature: "Columns",
        title: "Column filters on Correspondents",
        description:
          "Every column on the Correspondents list has a filter under its header.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "List",
        feature: "Columns",
        title: "Account Manager and Structurer columns",
        description:
          "Account Manager and Structurer are separate columns, and people show their full name with their email below, including who made the last pricing change.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "List",
        feature: "Rows",
        title: "Open a correspondent from its row",
        description:
          "Clicking anywhere on a row opens that correspondent's page.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "List",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "The list runs full width under a compact toolbar, the filters read All Tiers and All Statuses, and the workflow column shows the workflow once when RTL and DSCR match.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Request Board",
        feature: "Cards",
        title: "Comment counts stand out",
        description:
          "A card's comment count shows in bold teal when it has comments.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Entity",
        feature: "Add entity",
        title: "Add entity panels start empty",
        description:
          "Fixed Add Parent Entity and Create Borrowing Entity sometimes opening with values from an earlier entry.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Entity",
        feature: "Edit entity",
        title: "Editing an entity saves only your changes",
        description:
          "Fixed saving an entity re-sending fields you didn't change, which could overwrite other entity details.",
      },
      {
        type: "fix",
        section: "Support",
        area: "Request Board",
        feature: "Cards",
        title: "Dragging cards in Requests",
        description:
          "Fixed dragging a card in the Requests column sometimes being treated as a click.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "General",
        area: "Forms",
        feature: "Date fields",
        title: "Four-digit years in date fields",
        description:
          "Fixed date fields accepting a year longer than four digits, which saved a bad date that then showed as empty.",
      },
      {
        type: "fix",
        section: "General",
        area: "Forms",
        feature: "Edit panels",
        title: "Save only turns on for real changes",
        description:
          "Fixed clearing an already empty field in an edit panel counting as a change and turning on Save.",
      },
    ],
  },
  {
    date: "2026-10-21T18:00:00",
    version: "2026.10.21",
    title: "Outside orders in the Appraisal Queue, UPB on acquisition loans and expired lock extensions",
    summary:
      "Track outside appraisal orders across loans in the Appraisal Queue, see UPB and a balance chart on acquisition loans, and extend an expired acquisition lock.",
    highlights: [
      {
        type: "feature",
        title: "Outside orders queue",
        summary: "Track every vendor, AMC and AVM order across loans in the Appraisal Queue.",
      },
      {
        type: "feature",
        title: "UPB at a glance",
        summary: "Acquisition loans show their current balance, how it got there and a balance chart to maturity.",
      },
      {
        type: "feature",
        title: "Expired locks can be extended",
        summary: "An expired lock on an acquisition loan stays in the lock view, ready to extend.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "Outside orders",
        title: "Outside orders in the Appraisal Queue",
        description:
          "A new Outside orders tab lists every vendor, AMC and AVM order across loans, with status, vendor, needed by date, received values, filters, counts and search.",
        roles: ["underwriter", "admin"],
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "Outside orders",
        title: "Work an outside order from the queue",
        description:
          "Clicking an order opens its details, history and the loan side by side, and you can mark it placed, received or cancelled without leaving the queue.",
        roles: ["underwriter", "admin"],
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "Outside orders",
        title: "Outside orders filters",
        description:
          "Narrow outside orders by Pre-Close or Acquisition, by Ordered by me, or include dead, inactive and closed loans, and orders past their needed by date are flagged past due.",
        roles: ["underwriter", "admin"],
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Summary",
        feature: "UPB",
        title: "UPB on acquisition loans",
        description:
          "Acquisition loans with a Current and Original UPB show a Current UPB card, whose breakdown runs from Original UPB to the purchase price, and a UPB over time chart from origination to maturity.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Trade Economics",
        feature: "UPB",
        title: "UPB section on Trade Economics",
        description:
          "Trade Economics has a UPB section with the same card and chart, plus note rate, maturity, next payment, paid to date, holdback left and interest reserve.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Summary",
        feature: "Loan information",
        title: "Current and Original UPB on the Summary",
        description:
          "The Loan information panel shows Current and Original UPB on acquisition loans, and clicking them opens Trade Economics.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Trade Economics",
        feature: "Recompute inputs",
        title: "Recompute inputs layout",
        description:
          "Each recompute input has its own row and dollar amounts format as you type. Accrued interest replaces Current UPB in the headline figures, and Current UPB now leads the UPB section.",
      },
      {
        type: "improvement",
        section: "Pipelines & Queues",
        area: "Acquisitions",
        feature: "Columns",
        title: "Original UPB column",
        description:
          "The Acquisitions pipeline shows Original UPB next to Current UPB by default.",
      },
      {
        type: "improvement",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Internal reviews and Outside orders share one header, stat cards and search style on a wider page, and an order's loan number opens the loan in a new tab.",
        roles: ["underwriter", "admin"],
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Expired locks",
        title: "Extending an expired acquisition lock",
        description:
          "Fixed an expired lock on an acquisition loan showing no active lock and no Extend option.",
      },
      {
        type: "fix",
        section: "Pipelines & Queues",
        area: "Appraisal Queue",
        feature: "Outside orders",
        title: "Acting on orders from the queue",
        description:
          "Fixed acting on an outside order from the queue reloading a different loan you had open.",
        roles: ["underwriter", "admin"],
      },
    ],
  },
  {
    date: "2026-10-20T18:00:00",
    version: "2026.10.20",
    title: "Outside party appraisal reviews, cleaner condition titles and hidden documents kept out of pickers",
    summary:
      "Order and track outside party appraisal reviews from the Orders tab, see conditions titled by their document type, add a Buy Rate on RTL and GUC loans, and pick from visible documents only.",
    highlights: [
      {
        type: "feature",
        title: "Outside party appraisal reviews",
        summary: "Order a review from an outside vendor or AMC and track it on the Orders tab.",
      },
      {
        type: "feature",
        title: "Cleaner condition titles",
        summary: "Document conditions are titled by the document type alone.",
      },
      {
        type: "feature",
        title: "Hidden documents stay hidden",
        summary: "Hidden files no longer show up when picking documents to link.",
      },
      {
        type: "feature",
        title: "Loan Info and acquisition dates",
        summary: "Buy Rate on RTL and GUC loans, and origination and settlement dates on acquisitions.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Orders",
        feature: "Appraisal Review",
        title: "Outside party appraisal reviews",
        description:
          "The Appraisal Review card can order a review from the internal appraisers or from an outside vendor or AMC, and Order review now asks which.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Orders",
        feature: "Appraisal Review",
        title: "Outside party orders on the card",
        description:
          "Outside party orders show under their property with a status, the vendor, a due date and who ordered them, alongside internal reviews.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Orders",
        feature: "Appraisal Review",
        title: "Track an outside order",
        description:
          "Mark an outside order placed with the vendor's order number, mark it received with its values and report, or cancel it with a reason.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Orders",
        feature: "Appraisal Review",
        title: "Outside order details",
        description:
          "Show order details lists the instructions, the appraisal files to check against, the received values and who did what, and every file opens a preview.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Add to Loan",
        feature: "Outside valuation",
        title: "Order an outside valuation",
        description:
          "Outside valuation under Orders in Add to Loan records a secondary valuation from a vendor or AMC, with a needed by date, instructions and the appraisal files to check against.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Pricing & Rate",
        title: "Buy Rate on RTL and GUC loans",
        description:
          "RTL and GUC loans have a Buy Rate field next to Interest Rate.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Header",
        feature: "Origination Date",
        title: "Origination Date on acquisitions",
        description:
          "The acquisition loan header shows the Origination Date next to Expected Settlement.",
      },
      {
        type: "feature",
        section: "General",
        area: "Profile",
        feature: "Auth token",
        title: "Auth token on your Profile",
        description:
          "Your Profile has an Auth token card to copy your sign in token for scripts, masked until shown and with its expiry.",
        roles: ["superAdmin"],
      },
      {
        type: "feature",
        section: "Support",
        area: "Request Board",
        feature: "Recurring Encounters",
        title: "Recurring Encounters on requests",
        description:
          "Click a card's Recurring Encounters counter each time an issue gets in your way, with Undo, and the request panel shows how often and by whom it was hit.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Support",
        area: "Request Board",
        feature: "Bulk moves",
        title: "Complete All and Approve All",
        description:
          "Frontend In Progress has Complete All and Completed - Awaiting Review has Approve All, each moving every card in the column after you confirm.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Support",
        area: "Request Board",
        feature: "History",
        title: "Encounters in history",
        description:
          "Logged encounters show in the request and board history, with an Encounters filter.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Orders",
        feature: "Appraisal Review",
        title: "Appraisal Review card",
        description:
          "Internal reviews carry an Internal appraisers tag and show people's names, the Orders totals count outside orders, and a property whose reviews were all cancelled can be ordered again.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Conditions",
        feature: "Condition titles",
        title: "Cleaner condition titles",
        description:
          "Document conditions are titled by the document type alone, without Missing Document:, and conditions from AI findings use the finding's heading, everywhere conditions appear.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Closer View",
        feature: "Settlement Date",
        title: "Settlement Date on acquisitions",
        description:
          "On acquisition loans the Closer View shows Settlement Date, settled or expected, instead of Close Date.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Request Board",
        feature: "Cards",
        title: "Request Board cards",
        description:
          "Requires Backend Changes cards show a small tag for the kind of change, with the details in the request panel, and Applies to is now Loan or area this relates to.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Document pickers",
        title: "Hidden documents in pickers",
        description:
          "Fixed hidden documents showing up as choices when picking files to attach or link, while one already linked stays listed so it can be removed.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Orders",
        feature: "Appraisal Review",
        title: "Orders changed by someone else",
        description:
          "Fixed acting on an order someone else just changed, which now explains what happened and refreshes the list.",
      },
      {
        type: "fix",
        section: "Support",
        area: "Request Board",
        feature: "Recurring Encounters",
        title: "Double counted encounters",
        description:
          "Fixed a quick double click counting an encounter twice, and errors on the board now explain what went wrong.",
        roles: ["admin"],
      },
    ],
  },
  {
    date: "2026-10-16T18:00:00",
    version: "2026.10.16",
    title: "Sizing by loan amount, exception requests and a changes bar on the sizers, delayed purchase on cash-out refinances, DSCR Sizer access, and document coverage in the Documents sidebar",
    summary:
      "Size RTL and GUC loans by amount, review your changes before re-pricing, request exceptions from RTL results, answer Delayed purchase on a cash-out refinance, and see document coverage in the Documents sidebar.",
    highlights: [
      {
        type: "feature",
        title: "Delayed purchase moves to a checkbox",
        summary: "No longer a loan purpose: mark any cash-out refinance as a delayed purchase with one checkbox.",
      },
      {
        type: "feature",
        title: "Sizer improvements",
        summary: "Size by leverage % or loan amount, request exceptions and review your changes before re-pricing, with updates across the RTL, GUC and DSCR Sizers.",
      },
      {
        type: "feature",
        title: "Loan Drilldown updates",
        summary: "Clicking a linked document chip now opens its preview, and a condition counts documents linked to it instead of reading Missing.",
      },
      {
        type: "feature",
        title: "Request Board is live",
        summary: "File, track and follow up on requests and fixes in one place.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Loan details",
        title: "Size a loan by leverage or loan amount",
        description:
          "The RTL and GUC Sizers have a Size the loan by choice, so a loan can be sized by requested leverage or by an initial loan amount, and a reopened scenario comes back on the option it used.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "Request an exception from an RTL finding",
        description:
          "Every hard or soft guideline finding on RTL Sizer results has a Request exception button that opens a request already filled in from that finding, and a finding with an open request shows Requested · pending.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "What fits card on RTL results",
        description:
          "A What fits card shows the largest loan that fits each limit the scenario is over, plus other loan amounts that pass, each with a Reprice at this amount button.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "Leverage reductions on RTL results",
        description:
          "The Leverage card lists every leverage reduction applied, with its reason, guideline reference and size.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Re-price card",
        title: "Explain this result from the re-price card",
        description:
          "The RTL and GUC Sizers' re-price card has an Explain this result button, so the explanation opens without scrolling to the results.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Prepayment penalty",
        title: "Seven year prepayment penalty option",
        description:
          "Prepayment penalty structure has a new 7% 6% 5% 4% 3% 2% 1% option at the top of the list, also on the DSCR sizers.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Changelog",
        feature: "Releases",
        title: "Keep a release off the Changelog page",
        description:
          "A release can be kept off the Changelog page while still showing in the Admin Deployment Review.",
        roles: ["superAdmin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Inputs",
        title: "See what changed on each input",
        description:
          "Every input changed since the version on screen shows a small was marker under it with the previous value.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Inputs",
        title: "Changes bar on the scenario page",
        description:
          "Editing an input slides up a bar showing how many inputs changed since the current version, with a Review list of each change and Explain this result, Discard and Re-price buttons.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Documents sidebar",
        title: "Document coverage in the Documents sidebar",
        description:
          "The Documents sidebar now shows the same coverage board as the Documents tab, with every document group and type, the legend, search, group filters and expand or collapse all.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Documents sidebar",
        title: "Upload from the Documents sidebar",
        description:
          "Uploading to a document type in the Documents sidebar adds the files to the upload review bar, already filed under that group and type.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Request Board",
        feature: "Request Board",
        title: "Request Board",
        description:
          "Support has a Request Board for filing and tracking feature requests and fixes, with columns from Requests through Approved, comments, backend notes and history.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Create Loan",
        feature: "Delayed purchase",
        title: "Delayed purchase on a cash-out refinance",
        description:
          "Delayed Purchase is no longer a loan purpose. A Delayed purchase Yes or No question shows when the purpose is Refinance - Cash Out, and older Delayed Purchase loans open as a cash-out refinance with Yes.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Delayed purchase",
        title: "Delayed purchase on Loan Info",
        description:
          "Loan Info shows Delayed purchase under Loan Purpose on a cash-out refinance, and a Yes brings up the purchase fields instead of the payoff fields.",
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Loan details",
        title: "Delayed purchase moved to cash-out refinance",
        description:
          "On the RTL and GUC Sizers, Delayed purchase is now asked on Refinance - Cash Out, and a Yes is priced as a purchase.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Loan details",
        title: "Delayed purchase on the DSCR Sizer",
        description:
          "The DSCR Sizer and Quick Entry ask Delayed purchase on a cash-out refinance, and pricing waits until it is answered.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Access",
        title: "DSCR Sizer for super admins only",
        description:
          "The DSCR Sizer now shows only for super admins. It is hidden for the Sizer Visibility group and other admins.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Sizers",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Buttons, chips and links across the sizers now use the portal's primary color.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "Hard exception banner on RTL results",
        description:
          "The red Hard exception banner now shows for any hard exception, and explains that the loan can be created but needs Account Coverage approval before it can enter processing.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Save as deal",
        title: "Loan amount when saving as a deal",
        description:
          "A scenario sized by loan amount shows its initial loan amount, instead of requested leverage, when it is saved as a deal.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "Scenario summary",
        title: "Loan amount on scenarios sized by amount",
        description:
          "The scenario summary and version history show the initial loan amount for scenarios sized by loan amount.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "Access",
        title: "DSCR scenarios for super admins only",
        description:
          "DSCR scenarios, the DSCR filter and the DSCR new scenario option on Priced Scenarios show only for super admins.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Property",
        title: "Units set by the property type",
        description:
          "On the DSCR Sizer the property type sets the number of units wherever the type implies one, and units are typed only for Multifamily, Mixed-Use, Commercial and Other.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Pricing",
        title: "Property type counts toward DSCR pricing",
        description:
          "The DSCR Sizer and DSCR Quick Entry now price with the property type, so the 2 to 4 and 5 to 9 unit adjustments apply.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Conditions",
        feature: "Documents",
        title: "Linked documents count toward a condition",
        description:
          "A condition's document count and list now include documents linked to it from elsewhere, so it no longer reads Missing when a linked document satisfies it.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Conditions",
        feature: "Linked documents",
        title: "Linked document chips open a preview",
        description:
          "Clicking a linked document chip, wherever an item shows what it is linked to, opens the file preview.",
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Loan details on the RTL and GUC Sizers fit on one row, with Delayed purchase joining it on a cash-out refinance.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Conditions",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Document chips show a hover state, and the misleading document count in a condition's Metadata is gone.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Changelog",
        feature: "Admin Deployment Review",
        title: "Admin Deployment Review",
        description:
          "The What's new pop-up is now the Admin Deployment Review, and each release has an Admin Deployment Review button that opens it again.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Support",
        area: "Changelog",
        feature: "Admins only label",
        title: "Admin label on changes",
        description:
          "Changes only admins can see now carry a yellow Admin label, shown once on a whole group when everything in it is admin only, and hovering it says who can see it.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "Liquidity Coverage on RTL and GUC results",
        description:
          "The liquidity line on RTL and GUC results is now labeled Liquidity Coverage and reads Sufficient Liquidity when it is covered.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Explain this result",
        title: "Every hard finding in Explain this result",
        description:
          "Explain this result now lists facility checks and takeout denials as hard findings, so a Hard exception banner always has its reasons in the explanation.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Results",
        title: "Open the explanation from the Hard exception banner",
        description:
          "The Hard exception banner has a button that opens the full explanation of the findings.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Scenario page",
        feature: "Re-price card",
        title: "Re-price card focus",
        description:
          "The card above the results now handles a first Calculate and price, re-running an unchanged scenario on today's rate sheet, and Explain this result, with the list of changes moved to the changes bar.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Scenario page",
        feature: "Header",
        title: "Unpriced changes in the scenario header",
        description:
          "The Inputs tab shows how many inputs have changed, and a Results still show warning naming the version appears beside the tabs while there are unpriced changes.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Sizers",
        feature: "Admin tools",
        title: "Admin tools menu on the sizers",
        description:
          "Fill demo scenario, Form spy and Payload moved into an Admin tools menu in the header of every sizer.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Coverage view",
        title: "Coverage view opens on what is on file",
        description:
          "The Documents tab coverage view now opens showing only the types with uploads, and one Show missing types checkbox with a count replaces the two hide switches.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Coverage view",
        title: "Upload from a document type's row",
        description:
          "In the coverage view and on document coverage cards, Upload and Assign now sit in each document type's header row.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Coverage cards",
        title: "Upload opens with your files",
        description:
          "Uploading to a type on a document coverage card opens the upload window with the chosen files already in it, under the right group and type.",
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Scenario page",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Results and Inputs use a pill switcher, the results column scrolls on its own beside the form, figures wrap instead of being cut off, and the scenario name loses its dashed underline.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "The coverage filters are renamed All Groups, Entity Groups, Applicant Groups and Property Groups, the toolbar wraps in narrow windows, and the Documents sidebar opens wider.",
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "Scenario summary",
        title: "Delayed purchase on scenario summaries",
        description:
          "A delayed purchase scenario shows Refinance - Cash Out with a delayed purchase note.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Property",
        title: "Units on DSCR Quick Entry",
        description:
          "DSCR Quick Entry asks for units on Multifamily, Mixed-Use and Commercial properties and counts them toward pricing.",
        roles: ["superAdmin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Liquidity Calc.",
        feature: "Required liquidity",
        title: "Delayed purchase in required liquidity",
        description:
          "A cash-out refinance marked as a delayed purchase is treated as a purchase for required liquidity.",
      },
      {
        type: "fix",
        section: "Sizers",
        area: "Max Leverage",
        feature: "Purpose",
        title: "Delayed Purchase removed from Max Leverage",
        description:
          "Fixed Delayed Purchase being offered as a purpose on Max Leverage, where it always returned an empty grid.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Sizers",
        area: "RTL Sizer",
        feature: "Loan details",
        title: "Delayed purchase answer resets",
        description:
          "Fixed the Delayed purchase answer sticking on the RTL and GUC Sizers after the purpose was changed away from Refinance - Cash Out.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Pipelines & Queues",
        area: "Create Loan",
        feature: "Properties",
        title: "Purchase price on refinances",
        description:
          "Fixed refinance loans not asking for the property's original purchase price and date.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Summary",
        feature: "Purchase card",
        title: "Purchase card on delayed purchases",
        description:
          "Fixed delayed purchase loans showing neither the Purchase nor the Refinance card on the Summary tab.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Files",
        feature: "Required documents",
        title: "Required documents on delayed purchases",
        description:
          "Fixed delayed purchase loans asking for a payoff letter instead of a purchase contract.",
      },
    ],
  },
  {
    date: "2026-10-15T18:00:00",
    version: "2026.10.15",
    title: "Sizers section and scenario pages, correspondent onboarding and Ocrolus authenticity",
    summary:
      "The sizers have their own section, each scenario opens on its own page with versions, review and re-pricing, correspondents get an Onboarding checklist tab and new document types, and Ocrolus shows authenticity per document.",
    highlights: [
      {
        type: "feature",
        title: "Admin Deployment Review",
        summary:
          "Reopen it any time from the Changelog page.",
      },
      {
        type: "feature",
        title: "Sizers in their own menu",
        summary:
          "A Sizers dropdown in the header, for admins and the Sizer Visibility group.",
      },
      {
        type: "feature",
        title: "Correspondent onboarding checklist",
        summary:
          "A new Onboarding tab, plus new document types.",
      },
      {
        type: "feature",
        title: "Document authenticity in Ocrolus",
        summary:
          "Each document Ocrolus checked gets a trust rating.",
      },
    ],
    changes: [
      {
        type: "feature",
        section: "Sizers",
        area: "Navigation",
        feature: "Sizers section",
        title: "Sizers section in the main header",
        description:
          "Sizers has its own dropdown in the main header with Priced Scenarios, RTL Sizer, GUC Sizer, DSCR Sizer and Max Leverage. The dropdown and its tabs show only for people in the Sizer Visibility group under Users > Groups, plus admins. Old links to the sizers under Admin open the same page here.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Review and Edit inputs",
        title: "One page per scenario",
        description:
          "A saved scenario opens on its own page with a Review and Edit inputs switch, replacing the old scenario detail page.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Header",
        title: "Scenario header",
        description:
          "The pinned header holds a back link, the name you can rename in place, the type, company, priced date and status, and an amber pill when there are unpriced changes.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Versions",
        title: "Version picker",
        description:
          "Pick any version to see when and by whom it was priced, its key inputs and how far it moved from v1. On an older version a banner offers Back to current and Edit from that version.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "More actions",
        title: "Scenario actions",
        description:
          "A more actions menu has Copy link, Duplicate as new scenario, View raw payload, and Archive with Undo or Reactivate.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Review",
        title: "Review a priced scenario",
        description:
          "Review shows the key figures, the full result cards and the inputs that priced the version on screen. GUC adds Explain this result.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Re-price",
        title: "Re-price card",
        description:
          "A card above the results shows what changed since the last version, with Re-price and Discard changes, and dims results that no longer match the inputs.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Scenario page",
        feature: "Pricing",
        title: "New version message",
        description:
          "After each price a message shows the new version, its headline figure and the change from the previous one, with a View results link.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "New scenario",
        title: "New scenario button",
        description:
          "A New scenario button starts a new RTL, GUC or DSCR sizer run.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Sizers",
        area: "RTL & GUC Sizer",
        feature: "Delayed purchase",
        title: "Delayed purchase question",
        description:
          "Delayed purchase? is its own required Yes or No field next to Purpose, shown only for purchases.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding",
        feature: "Onboarding checklist",
        title: "Onboarding tab on correspondents",
        description:
          "A new Onboarding tab holds the onboarding checklist as a form, from initial engagement and legal review through agreement execution, post-execution documents and go-live sign-off, with a History of every saved change.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding",
        feature: "Onboarding checklist",
        title: "Onboarding progress",
        description:
          "A progress bar and the tab label show how many required documents are done, with a Signed off badge once final sign-off is recorded.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Onboarding",
        feature: "Document items",
        title: "Edit and upload onboarding documents",
        description:
          "Each required document has its own edit dialog for status, dates, notes and linked documents, and Upload a new document links the file to the item and adds it to Agreements.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Agreements",
        feature: "Document types",
        title: "New correspondent document types",
        description:
          "Broker Agreement is versioned like the other agreements, and Prospect Questionnaire, Onboarding Checklist and Background Authorization are new document types.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Agreements",
        feature: "Blank forms",
        title: "Blank forms link",
        description:
          "A Blank forms link on the Agreements and Onboarding tabs opens the team's folder of current blank questionnaires, checklists and agreements.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Ocrolus",
        title: "Document authenticity on Ocrolus",
        description:
          "A Document authenticity table lists every analyzed document with its score, rating, reason codes and risk signals, worst first, with a preview and an Open in Ocrolus button. Books graded before this offer Re-sync with Ocrolus.",
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "Access",
        title: "Your own scenarios in the Sizer Visibility group",
        description:
          "Members of the Sizer Visibility group who are not admins see only the scenarios they priced.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "List",
        title: "Priced Scenarios list",
        description:
          "Rows show the type, name, address and loan details, Loan amount, Req. leverage, Priced by and Last priced. Clicking a row opens it in Review, and hovering shows Edit & re-price.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Priced Scenarios",
        feature: "Filters",
        title: "Priced Scenarios filters",
        description:
          "Active, Converted and Archived tabs and a type chip row show counts, admins get an Only my scenarios switch, and pressing / jumps to search.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "RTL & GUC Sizer",
        feature: "Save as deal",
        title: "Save as deal in the header",
        description:
          "Save as deal sits in the scenario header, waits until changed inputs are re-priced, and becomes Open loan once the scenario is a deal.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Required fields",
        title: "Required fields on the DSCR Sizer",
        description:
          "Pricing waits until Purpose, Amortization, Loan amount and As-is value are filled, and the message names what is missing.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Rural exception",
        title: "Rural exception kept on re-price",
        description:
          "A requested rural exception stays on the scenario and carries into later re-prices.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Sizers",
        area: "Sizers",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Wider sizer pages with a pinned results column. The scenario header and re-price card replace the old scenario banner and bottom action bar.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Agreements",
        feature: "Document type",
        title: "Searchable document pickers",
        description:
          "The Document type and upload Category pickers can be searched and are sorted A to Z, with versioned agreements tagged Versioned.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Ocrolus",
        title: "Authenticity on the Ocrolus status panel",
        description:
          "The status panel shows an Authenticity risk badge, how many documents need review and separate risk, trust and info counts, and clicking it jumps to the authenticity table.",
      },
      {
        type: "fix",
        section: "Sizers",
        area: "RTL & GUC Sizer",
        feature: "Loan amounts",
        title: "Frozen loan amounts",
        description:
          "Fixed an opened scenario's loan amounts not updating when leverage or budget changed.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Sizers",
        area: "DSCR Sizer",
        feature: "Validation",
        title: "DSCR Sizer error messages",
        description:
          "Fixed validation errors showing raw data, and errors from one scenario carrying over to the next.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Correspondents",
        area: "Correspondent page",
        feature: "Layout",
        title: "Empty band under long tabs",
        description:
          "Fixed an empty white band under long correspondent tabs that the page could scroll into.",
      },
    ],
  },
  {
    date: "2026-10-14T18:00:00",
    version: "2026.10.14",
    title: "Manual address entry, correspondent documents and notification links",
    summary:
      "Addresses can always be entered by hand with a unit, 5 to 9 unit loans get an annual operating expense, correspondents hold every document type, Priced Scenarios filter by any mix, and notifications open the tab they are about.",
    changes: [
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Manual address entry always available",
        description:
          "When adding a property or entity address, a Not the right address? line under the matches lets you enter it by hand, not only when Google finds nothing.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Unit field on manual addresses",
        description:
          "Manual entry has a Unit / apt. field, and the saved full address includes the unit.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "DSCR Calc. & DSCR Workbook",
        feature: "Expenses",
        title: "Annual operating expenses for 5 to 9 units",
        description:
          "A new Annual Operating Expenses (5-9 Units) expense type is entered as a yearly amount and divided by 12. It is listed first and selected by default on 5 to 9 unit loans.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Agreements",
        feature: "Document type",
        title: "Every correspondent document on Agreements",
        description:
          "The Document type picker lists NDA, W9, Wiring Instructions, Corporate Docs, Licensing, Insurance and Other alongside the three versioned agreements. Other documents show as a plain list, newest first, with Upload document and Download.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "AI Review",
        feature: "Notification links",
        title: "AI notifications open the right section",
        description:
          "An AI notification opens its section of the AI Review tab (Hard checks, Conditions, Updates or Agents) instead of always opening Agents.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "File preview",
        title: "Smoother file preview resizing",
        description:
          "Dragging the preview panel's edge follows the cursor and stops exactly at its limits, and a spreadsheet preview holds still while you drag.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Summary",
        feature: "Stage tracker",
        title: "Acquisition sub-stage steps hidden",
        description:
          "The sub-stage steps under the acquisition stage tracker are hidden while the steps are finalized.",
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Sizers",
        feature: "Priced Scenarios",
        title: "Status and Type chips on Priced Scenarios",
        description:
          "The Status and Type filters are toggle chips, so you can show any mix, such as RTL and GUC together.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "General",
        area: "Notifications",
        feature: "Opening a notification",
        title: "Notifications open the right tab",
        description:
          "A loan notification opens the tab it is about, such as Exceptions, Conditions, Documents, Checklist, Loan Lock or Closing Readiness, instead of the loan's default view.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Unit landing in the City field",
        description:
          "Fixed a typed unit such as Unit 6F or Apt 2 filling the City field when switching to manual entry, here and in the RTL and GUC Sizers.",
      },
      {
        type: "fix",
        section: "Admin",
        area: "Sizers",
        feature: "Priced Scenarios",
        title: "Missing priced scenarios",
        description:
          "Fixed scenarios going missing from the list when several statuses were shown and there were more than 200 results.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "General",
        area: "Notifications",
        feature: "Opening a notification",
        title: "Notification links to a record",
        description:
          "Fixed notifications that link to a page opening the page but not the record it is about.",
      },
      {
        type: "fix",
        section: "General",
        area: "Forms",
        feature: "File upload",
        title: "Dropping files onto upload areas",
        description:
          "Fixed dropping a file onto an upload area doing nothing in some browsers. The area also opens with Enter or Space.",
      },
    ],
  },
  {
    date: "2026-10-09T18:00:00",
    version: "2026.10.09",
    title: "DSCR guideline and leverage checks, RTL rates by tier, RTL tier column and sizer cleanup",
    summary:
      "DSCR sizer results now show guideline fails, exception requests and the leverage grid with its reductions, Pricing Config shows RTL and GUC rates by tier with a what-if stepper, the Correspondents list has an RTL tier column, and the RTL and GUC sizers and Priced Scenarios are split cleanly.",
    changes: [
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "DSCR guideline check",
        title: "Guideline check on DSCR results",
        description:
          "DSCR Sizer and DSCR Quick Entry results show a Guideline check card, with a red banner and the reasons when a scenario fails the guidelines (such as a loan over $3M or a FICO under 660), while the price still shows as an indication.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "DSCR guideline check",
        title: "Exception request flag on DSCR results",
        description:
          "A DSCR between 0.80 and 0.99 is flagged Requires exception request instead of failing.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "DSCR leverage",
        title: "Leverage card on DSCR results",
        description:
          "A Leverage card shows the max LTV (and LTC on purchases) after reductions next to the requested figure, a chip for each reduction applied, and the loan amount that fits when the request is over.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "DSCR Sizer",
        title: "Recent renovation question on the DSCR Sizer",
        description:
          "A vacant refinance asks whether the property was recently renovated or built, which removes the 5% vacant refinance leverage reduction.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "Pricing Config",
        title: "RTL and GUC rates by tier on Pricing Config",
        description:
          "Pricing Config shows each RTL product's base rate and the rate every correspondent tier gets, with a Change stepper that previews moving every tier by 0.125% steps.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "List",
        feature: "RTL tier",
        title: "RTL Tier column on Correspondents",
        description:
          "The Correspondents list has an RTL Tier column next to DSCR Tier, with tiers inherited from the DSCR tier shown faded, and the Tier filter matches either column.",
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Pricing",
        feature: "Pricing Config",
        title: "Empty base rate grid removed from Pricing Config",
        description:
          "The empty Base rates by product and term grid was removed from Pricing Config, since live base rates are set on RTL & GUC Pricing.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Sizers",
        feature: "RTL Sizer",
        title: "Ground up construction priced in the GUC Sizer only",
        description:
          "The RTL Sizer now offers Fix and Flip and Stabilized Bridge only, since ground up construction is priced in the GUC Sizer.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Sizers",
        feature: "Purpose",
        title: "Delayed purchase is a flag on Purchase",
        description:
          "On the RTL and GUC Sizers, purpose is Purchase or one of the two refinances, and a Delayed purchase Yes or No switch appears under Purchase.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Sizers",
        feature: "Priced Scenarios",
        title: "RTL and GUC scenarios listed separately",
        description:
          "Priced Scenarios shows RTL and GUC as separate types in the Type column and the Type filter.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Admin",
        area: "Sizers",
        feature: "Priced Scenarios",
        title: "GUC scenarios open in the GUC Sizer",
        description:
          "Fixed Open in sizer on a ground up construction scenario opening the RTL Sizer instead of the GUC Sizer.",
        roles: ["admin"],
      },
    ],
  },
  {
    date: "2026-10-08T18:00:00",
    version: "2026.10.08",
    title: "RTL tier pricing, correspondent pricing, interest reserves and document grouping",
    summary:
      "RTL tier spreads and last edits on Admin Pricing, RTL tier and overlay pricing on correspondents, a dated DSCR sheet history, interest reserves on every product except DSCR, Documents grouping in status order, and Full Pipeline views saved to your profile.",
    changes: [
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "Tier spreads",
        title: "RTL tier spreads on Admin Pricing",
        description:
          "RTL & GUC Pricing has a Tier spreads section showing each tier's account spread in percent, its status, loan range, history and last change, and it is the list correspondents pick their RTL tier from. Tiers default to Gold +0.125%, Silver +0.25% and Bronze +0.375%.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "Tier spreads",
        title: "Add or edit an RTL tier",
        description:
          "A tier is added or edited in a dialog with a tier dropdown, a spread field whose arrows step by 0.125%, and an Active switch that retires a tier without deleting it.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "RTL & GUC Pricing",
        title: "Last edit on each RTL pricing section",
        description:
          "Every section header on RTL & GUC Pricing shows when it was last edited, by whom, and the note saved with it.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "Pricing Config",
        title: "Last change on Pricing Config tables",
        description:
          "DSCR tier margins and RTL base rates have a Last change column that shows what moved on hover, and each section header names the newest edit.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "DSCR Pricing",
        title: "Sheet as of date on DSCR pricing",
        description:
          "The Add DSCR pricing wizard asks which day the sheet reflects, defaulting to today, and that date shows on the sheet, in the Version picker and in the version history.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Pricing",
        feature: "DSCR Pricing",
        title: "Captured sheet days on DSCR pricing",
        description:
          "A strip above the version history lists the days a counterparty's sheet was captured, and clicking a day opens that version.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "DSCR Sizer",
        title: "DSCR sizers price off Resfin base",
        description:
          "The DSCR Sizer and DSCR Quick Entry now price off the Resfin base sheet, so the Pricing sheet picker was removed.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "RTL and GUC rate",
        title: "Required coupon on RTL and GUC results",
        description:
          "RTL and GUC results, the priced scenarios list and the rate preview on RTL & GUC Pricing now lead with the required coupon the borrower pays, with the buy rate shown below it.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Admin",
        area: "Sizers",
        feature: "DSCR Sizer",
        title: "Pricing sheet named on DSCR results",
        description:
          "The result line on the DSCR Sizer and DSCR Quick Entry names the sheet that priced the scenario.",
        roles: ["admin"],
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Pricing",
        feature: "RTL / GUC pricing",
        title: "RTL pricing tier on correspondents",
        description:
          "A correspondent's Pricing tab assigns an RTL pricing tier from the tier table, with each tier's spread shown in the dropdown. Tiers the desk hasn't priced yet are flagged instead of reading as 0%.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Pricing",
        feature: "RTL / GUC pricing",
        title: "Spread overlay on correspondents",
        description:
          "A spread overlay in percent, which can be negative, adds on top of every RTL product, and a note is required to save whenever it isn't 0.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Pricing",
        feature: "RTL / GUC pricing",
        title: "Per-product spreads as overrides",
        description:
          "Per-product spreads now override the tier, and each product shows the spread it actually prices at, with the overlay included and where it comes from.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Pricing",
        feature: "RTL / GUC pricing",
        title: "RTL follows the correspondent tier",
        description:
          "With no RTL tier picked, RTL and GUC price on the correspondent's own tier, and the dropdown and each product say so. Pick an RTL tier only when it should differ.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "Pricing",
        feature: "Last pricing change",
        title: "Last pricing change on the Pricing tab",
        description:
          "A strip at the top of the Pricing tab shows the last pricing change: when, who, which fields and why.",
      },
      {
        type: "feature",
        section: "Correspondents",
        area: "List",
        feature: "Last pricing change",
        title: "Last Pricing Change column on Correspondents",
        description:
          "The Correspondents list has a Last Pricing Change column, sortable by date, with the fields and reason on hover.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Budget",
        title: "Interest reserves on the property Budget subtab",
        description:
          "Every loan product except DSCR now has the Budget subtab in the property sidebar, and loans that aren't construction see just Interest Reserves and Interest Reserve Financed? there.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Property card",
        title: "Interest reserves on the Loan Info property card",
        description:
          "Interest Reserves and Interest Reserve Financed? now show on the property card for every product except DSCR.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Tickets",
        feature: "Notes",
        title: "Notes on support tickets",
        description:
          "A ticket page has a Notes section with the ticket's thread, including replies from its Slack thread tagged via Slack, and an Add note box whose notes are also posted to Slack.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Tickets",
        feature: "Filed from Slack",
        title: "Filed from Slack tag",
        description:
          "A ticket filed from Slack shows a Filed from Slack tag in its header.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Grouping",
        title: "Status groups in dropdown order",
        description:
          "Grouping the Documents table by Status lists the groups in the status dropdown's order, from New Upload to Underwriter Accepted, and sorting no longer reshuffles them.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Table settings",
        title: "Grouped column stays visible",
        description:
          "The Hide grouped columns setting was removed, so the column you group by stays in the table.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Payments & Reserves",
        title: "No interest reserve rows on DSCR loans",
        description:
          "DSCR loans no longer show interest reserve rows under Payments & Reserves.",
      },
      {
        type: "improvement",
        section: "Pipelines & Queues",
        area: "Pipeline boards",
        feature: "Saved views",
        title: "Full Pipeline views saved to your profile",
        description:
          "Full Pipeline layouts and your default layout are saved to your profile, so they follow you to another browser or computer.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Pricing",
        feature: "Saving",
        title: "Save pricing from the changes bar",
        description:
          "Pricing changes save from the bar at the bottom of the screen, with Save and Discard.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Pricing",
        feature: "RTL / GUC pricing",
        title: "Separate saves for RTL, DSCR and terms",
        description:
          "Saving the Pricing tab sends RTL, DSCR and terms or lock changes separately and only what changed, so one card can no longer overwrite another. Saving RTL or DSCR changes needs a reason.",
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Pricing",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "The Pricing tab is one column with an On this page rail like Profile, labels and helper text are restyled on Pricing and Profile with more room between fields, and the list is wider.",
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Pricing",
        feature: "DSCR Pricing",
        title: "ResFin base leads the counterparty list",
        description:
          "The ResFin base sheet now comes first in the DSCR counterparty list, ahead of Eastview base.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Header",
        feature: "Admin menu",
        title: "Pricing and Sizers flyouts in the Admin menu",
        description:
          "Hovering Pricing or Sizers in the Admin dropdown opens a flyout with their tabs, so you can jump straight to one.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Pricing",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "The Add DSCR pricing wizard and the tier dialog use the standard form layout, with drag and drop upload, a step counter and buttons in the footer, and the DSCR version history sits closer under Captured sheet days.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        section: "Correspondents",
        area: "Pricing",
        feature: "Saving",
        title: "Save pricing points to what's missing",
        description:
          "Clicking Save pricing with the reason or the overlay note missing now scrolls to that field and puts the cursor in it, instead of appearing to do nothing.",
      },
      {
        type: "improvement",
        section: "Admin",
        area: "Pricing",
        feature: "DSCR Pricing",
        title: "Coupon ladder row counts only as a warning",
        description:
          "The coupon ladder's tier tabs show a row count only when a tier has no rows or the tiers don't match, so a count now means something to check.",
        roles: ["admin"],
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Rename",
        title: "File names with dots",
        description:
          "Fixed file names with dots in them, such as dates, being cut short when renamed.",
      },
      {
        type: "fix",
        section: "Correspondents",
        area: "Pricing",
        feature: "Audit trail",
        title: "Audit trail values",
        description:
          "Fixed per-product spread and extension fee preset changes showing as [object Object] in the audit trail.",
      },
      {
        type: "fix",
        section: "Correspondents",
        area: "Pricing",
        feature: "Audit trail",
        title: "Untouched RTL fields in the audit trail",
        description:
          "Fixed saving a correspondent's pricing logging audit entries for RTL fields that weren't changed.",
      },
    ],
  },
  {
    date: "2026-10-06T18:00:00",
    version: "2026.10.06",
    title: "Settlement workbook, final budgets and inspections, HUD tie-out, document bulk actions and lock request rules",
    summary:
      "A settlement workbook on the Closing tab, final budgets, budget sources, Day 1 draws and site inspection imports on Budget & Draws, a choosable HUD source and lender charges check on Wire & Funding, bulk actions, packages and a reworked toolbar and grouping on the Documents tab, a sizer and required price inputs to request a lock, collapsible contact sections, pipeline grouping and reset, and a redesigned What's new page.",
    changes: [
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Bulk actions",
        title: "Bulk actions on the Documents tab",
        description:
          "Checking files opens an action bar at the bottom of the screen with Status, Type, Link to, Validity, Edit all, Download and More. Each choice shows how many of the selected files already have it, and every change can be undone from the message that follows.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Bulk actions",
        title: "Edit all selected documents at once",
        description:
          "Edit all opens a side panel for status, type, a link, validity and the sharing and visibility flags together, and only the fields you change are saved.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Bulk actions",
        title: "Selecting documents faster",
        description:
          "Click the selected count to select all shown files, clear the selection or show only the selected files. Shift-click selects a range, and S, T, L, V and E open the bulk actions.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Packages",
        title: "Packages on the Documents tab",
        description:
          "Each package shows its files, which are ready or left out and why, and when it was last saved to the loan. You can preview its files, filter the table to them, download the zip or stage it on the loan.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Related Contacts",
        feature: "Edit Contact",
        title: "Remove a contact from the loan",
        description:
          "Editing a loan contact has a trash button in the sidebar header that removes them from the loan after you confirm. They stay in the contact directory.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Pipeline boards",
        feature: "Toolbar",
        title: "Reset view on the pipelines",
        description:
          "A Reset view button in the pipeline toolbar puts the columns, filters, sort and search back to the default. It replaces Reset Table in the saved layouts menu.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Pipeline boards",
        feature: "Table",
        title: "Group pipeline rows by any column",
        description:
          "Drag a column header into the bar above the table to group rows by it. The total row shows the loan count, and loans with no one in a role group under Unassigned.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Acquisitions",
        feature: "Columns",
        title: "Days In Current Status on the Acquisitions Pipeline",
        description:
          "A new column shows how many days each loan has been in its current status.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "Tie-Out",
        title: "Settlement statement on the Wire & Funding Tie-Out",
        description:
          "The HUD balance now names the settlement statement it was built from, with its type and whether it is executed, and the file opens in a preview.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Changelog",
        feature: "Layout",
        title: "What's new page",
        description:
          "Each release's changes are grouped by where they live in the portal, one card per area such as Loan Drilldown, then by screen, with every change labelled New, Improved or Fixed.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Changelog",
        feature: "Releases",
        title: "Pick a release",
        description:
          "One release shows at a time. Pick another from the date in the top corner, where the newest is marked Latest.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Changelog",
        feature: "Collapsing",
        title: "Collapse groups",
        description:
          "Every area card and screen can be collapsed, and Collapse all folds the release down to a short list of areas.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Changelog",
        feature: "Collapsing",
        title: "Guide lines",
        description:
          "A guide line runs down from each open group's arrow, and clicking it collapses that group.",
      },
      {
        type: "feature",
        section: "Support",
        area: "Changelog",
        feature: "Search",
        title: "Search the changelog",
        description:
          "Search a release's changes by any word with the matches highlighted, and the page lists the other releases that also have matches so you can jump to them.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Closing",
        feature: "Settlement Workbook",
        title: "Settlement Workbook on the Closing tab",
        description:
          "A new Settlement Workbook sub-tab reads a settlement statement line by line on pre-close loans, with the statement PDF beside the lines.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Closing",
        feature: "Settlement Workbook",
        title: "Build a settlement workbook",
        description:
          "Pick the statement to build from or upload a new one, then read every line or quick seed from an existing extract. Rebuilding warns that it discards your edits.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Closing",
        feature: "Settlement Workbook",
        title: "Edit settlement lines",
        description:
          "Every line can be edited in place, added by hand or reset, and hovering an edited line shows who changed what. Selected lines can be grouped, given a funding source or left out of totals at once.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Closing",
        feature: "Settlement Workbook",
        title: "Settlement workbook balance",
        description:
          "A balance panel checks each printed total against the lines and shows cash to close, the wire to title against the Net Wire Calculator, and totals by funding source and group.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Closing",
        feature: "Settlement Workbook",
        title: "Review a settlement workbook",
        description:
          "Mark the workbook Reviewed or back to Draft. The workbook never changes the loan or the Fees tab.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "HUD source",
        title: "Choose the HUD to balance against",
        description:
          "Change which settlement statement the Tie-Out balances against, or set it to Automatic, from a list showing each statement's type, status and borrower totals. A new statement can be uploaded from the same window.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "HUD source",
        title: "Newer settlement statement notice",
        description:
          "When a newer settlement statement is uploaded, a banner offers Use this one or Keep current, and the reconciliation bar flags it too.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "Lender charges",
        title: "Lender charges tie-out",
        description:
          "A new Lender charges check compares what the HUD charges the borrower for the lender with what was withheld from the wire, on the Tie-Out, the reconciliation bar and the wire ticket checklist.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Fees",
        feature: "Wire Instructions",
        title: "Incoming wires from title",
        description:
          "Wire Amount accepts a negative amount for a wire coming in from title, labelled Incoming from title, and the Net Wire Calculator and wire ticket label it the same way.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Final budget",
        title: "Lock a final budget",
        description:
          "Lock a property's budget sheet as the final budget that servicing reads. A locked sheet is read-only with a Final budget chip, and Unlock reopens it after a confirmation.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Budget source",
        title: "Choose a budget source",
        description:
          "Pick which budget a sheet comes from, such as a budget document, a feasibility review, an appraisal renovation budget or Manual, after comparing every budget the loan holds for the property.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Delete draw",
        title: "Delete the latest draw",
        description:
          "Delete the most recent draw after a confirmation, and drawn-to-date and the remaining holdback recalculate. Earlier draws and seller draws can't be deleted.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Site inspections",
        title: "Import site inspections",
        description:
          "Upload site inspection reports from the Draws section or a draw's row. Each is read and listed under the draws with its status, and a draw's inspection text links to its report.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Site inspections",
        title: "Apply an inspection to a draw",
        description:
          "Review what a report says and where it disagrees with the draw, correct it, then apply it to its draw or create the next draw from it.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "New draw",
        title: "Day 1 draw",
        description:
          "A new draw can be a Day 1 draw funded at closing. It skips the inspection, fills in the closing date and, on pre-close loans, marks the draw as funded in the closing wire.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Request Lock",
        title: "Sizer required to request a lock",
        description:
          "A sizer must be attached before a lock can be requested or resubmitted, on pre-close and acquisition loans. On pre-close loans you can upload one or pick one already on the loan.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Request Lock",
        title: "Required price inputs on the lock request",
        description:
          "Rate at lock and Calculated DSCR are required on DSCR loans, and Coupon rate on RTL loans. Hovering a disabled send button says what is still missing.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Request Lock",
        title: "Lock request form layout",
        description:
          "The request form opens in a wider panel with Price inputs in three columns, Sizer and Lock options sections, and a pinned footer showing what is left to fill in.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Related Contacts",
        feature: "Sections",
        title: "Collapsible sections on Loan Related Contacts",
        description:
          "Key roles, General contractors and All contacts can each be collapsed. Each general contractor opens from its row, and Key roles shows a count that turns teal when every role is filled.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Related Contacts",
        feature: "General contractors",
        title: "Experience Calculator on Loan Related Contacts",
        description:
          "The Experience Calculator button moved into the GC qualifying experience summary, which now shows whenever the loan has contractors.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Table settings",
        title: "Documents table settings",
        description:
          "Table settings now offer compact rows, category icons, the row group bar and hiding the grouped column, and an optional JSON column shows a document's raw data.",
      },
      {
        type: "improvement",
        section: "Pipelines & Queues",
        area: "Acquisitions",
        feature: "Table",
        title: "Acquisitions grouped by Loan Status",
        description:
          "Status groups follow the trade stages in order, from New Trade through Boarded, then Kicked, Declined and Inactive. On the Full Pipeline, pre-close statuses come first.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Conditions Checklist",
        feature: "Missing items email",
        title: "Missing items email on the Conditions Checklist",
        description:
          "Missing documents are listed with the other missing items by their document type alone.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Loan Lock & Related Contacts",
        feature: "General UI improvements",
        title: "General UI improvements to the lock and contact screens",
        description:
          "The lock forms and lock panels use the teal color scheme, and the Loan Related Contacts tab has a pinned section bar and a simpler contractor layout.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Changelog",
        feature: "Tabs",
        title: "Filter by kind of change",
        description:
          "Tabs filter every area to its New features, Improved / updated or Fixed / bugs, each with its count, and hide areas with nothing left.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Changelog",
        feature: "Releases",
        title: "Release dates and links",
        description:
          "Releases are shown by their date, like 9/29/2026, and the link button copies a link to that release.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "HUD check",
        title: "No HUD to compare",
        description:
          "When no HUD has been read yet, the Tie-Out, reconciliation bar and wire ticket say No HUD to compare instead of reporting the loan out of balance.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "Reconciliation bar",
        title: "Statement on the reconciliation bar",
        description:
          "The reconciliation bar names the settlement statement it balances against, and the file opens in a preview.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Wire & Funding",
        feature: "General UI improvements",
        title: "General UI improvements to Wire & Funding",
        description:
          "Red alerts on the tab use darker text so they are easier to read.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "DSCR Calc. & DSCR Workbook",
        feature: "Monthly P&I",
        title: "Payment basis under Monthly P&I",
        description:
          "The line under Monthly P&I says whether the payment is interest-only or 30-year amortizing, with the loan amount, the rate and the loan's amortization type, and it reads the same on both tabs.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Changelog",
        feature: "Layout",
        title: "Changelog cards",
        description:
          "The page sits on a gray background with each area in its own card, and area and screen headings show how many changes are New, Improved and Fixed.",
      },
      {
        type: "improvement",
        section: "Support",
        area: "Changelog",
        feature: "Layout",
        title: "Changelog list style",
        description:
          "Each change starts with a green New, blue Improved or red Fixed label, a feature with one change reads on a single line, and collapsed cards have even spacing.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Construction capital",
        title: "Final budgets on Construction capital",
        description:
          "The Construction capital band shows how many budgets are final and their locked total, and the holdback warning now counts only final budgets.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Closing",
        feature: "Settlement Workbook",
        title: "Settlement workbook source",
        description:
          "A Built from bar names the statement the workbook came from, with View and Rebuild, and the Net Wire Tie-out says when it balances against that statement.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Bulk actions",
        title: "Send to Ocrolus from a mixed selection",
        description:
          "Send to Ocrolus works on any selection and sends only the PDF bank statements in it, saying how many other files won't be sent.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Toolbar",
        title: "Documents toolbar",
        description:
          "Reset sits next to Sort and also clears the grouping, Columns and Table settings are icon buttons, Expand and Collapse are separate buttons, and the search box shrinks on smaller screens.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Table settings",
        title: "Fewer documents table settings",
        description:
          "The file preview and default group expansion settings were removed. Files open in the side panel and grouped tables open with the top level expanded.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Grouping",
        title: "Grouped documents table",
        description:
          "A guide line runs down from each group's arrow, tinted in the category's color, and clicking it collapses that group. Categories and types list A to Z, and group checkboxes match the file checkboxes.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Table",
        title: "Documents table height",
        description:
          "The table reaches the bottom of the screen even when only a few rows show, so the Columns panel has room.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "New draw",
        title: "Property on a new draw",
        description:
          "A property must be picked for a new draw on loans with more than one property.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Inspection photos",
        title: "Inspection photos by property",
        description:
          "On loans with more than one property, inspection reports are grouped by property and then by draw, and photos taken from the appraisal are tagged From appraisal.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Official address match",
        description:
          "Addresses no longer count as different just because one spells out a street type or direction and the other abbreviates it, such as Avenue and Ave or Southwest and SW.",
      },
      {
        type: "improvement",
        section: "General",
        area: "Header",
        feature: "Pinned tabs",
        title: "Pinned tabs handle removed",
        description:
          "The hover handle under the header that reopened hidden pinned tabs was removed.",
      },
      {
        type: "fix",
        section: "Pipelines & Queues",
        area: "Pipeline boards",
        feature: "Table",
        title: "Pipeline columns dragged off the table",
        description:
          "Fixed a column disappearing when its header was dragged off the table.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Edit Lock",
        title: "Edit Loan Lock on a requested lock",
        description:
          "Fixed Edit Loan Lock resending every field and offering a sizer upload that was never saved. It now saves only what you change.",
        roles: ["capitalMarkets"],
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Acquisition locks",
        title: "Existing locks on acquisition loans",
        description:
          "Fixed acquisition loans with a requested or confirmed lock, including one synced from Salesforce, showing no active lock.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "DSCR Calc. & DSCR Workbook",
        feature: "Monthly P&I",
        title: "Interest rate under Monthly P&I",
        description:
          "Fixed the interest rate under Monthly P&I showing as a tiny fraction, such as 0.079% for a 7.875% rate.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Request Lock",
        title: "Sizer document type",
        description:
          "Fixed a sizer uploaded with a lock request being saved with no document type. It is now filed as a Sizer under Terms & Sizer.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Package column",
        title: "Package column after unflagging",
        description:
          "Fixed the Package column still naming a package after the file was removed from every package.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Table",
        title: "Documents table layout fixes",
        description:
          "Fixed the checkbox column staying wide after ungrouping, the Filename header being cut off when grouped, and the status color not filling the cell in compact rows.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Budget & Draws",
        feature: "Feasibility",
        title: "Desk reviews using appraisal photos",
        description:
          "Fixed a review that used the appraisal's photos reading as a site visit. It now says it is a desk review using the appraisal's photos.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Street number and street name",
        description:
          "Fixed the street number and street name when the whole street line was stored as the street name, such as on loans from the acquisitions sync.",
      },
    ],
  },
  {
    date: "2026-10-02T18:00:00",
    version: "2026.10.02",
    title: "Classification override, applicant verifications, lock forms and acquisition stages",
    summary:
      "Borrower classification overrides, verification boxes on applicants, a new property address and rent roll, separate acquisition lock forms with lock notes, quick edits from closing readiness, and acquisitions on hold.",
    changes: [
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Borrower Classification Override",
        title: "Borrower classification override on Loan Info",
        description:
          "In the Underwriting card, underwriters and admins can override the borrower classification with a required note. The override gets its own row where it can be edited or backed by an exception, and the Experience Calc. tab shows it too.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Applicants",
        feature: "Verification boxes",
        title: "Verification boxes on the applicant card",
        description:
          "Credit Auth, Credit Report and Background Check each show their file, date and status. Click one to upload or assign a file, preview or remove it and set its date, with a warning when it expires before closing.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Applicants",
        feature: "Applicant sidebar",
        title: "Key documents in the applicant sidebar",
        description:
          "The applicant sidebar has the same verification boxes plus a Photo ID box with its expiration date, and its Documents tab matches the applicant card.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Property address in the property sidebar",
        description:
          "The Address tab shows whether the address is verified. Change address lets you search for an address or enter it by hand and compare it with the current one before saving, and the address on title can be added or edited there.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Units",
        title: "Rent roll on the property Units tab",
        description:
          "A summary of leased and vacant units and in-place against market rent, monthly or annual, with units added and edited right in the table.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Acquisition lock forms",
        title: "Acquisition lock forms",
        description:
          "Requesting, confirming and cancelling a lock on an acquisition opens its own form in the Loan Lock sidebar, separate from the pre-close form.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Timeline and previous locks",
        title: "Lock notes and previous locks on the Loan Lock tab",
        description:
          "The timeline shows each extension request and its outcome, lock notes have their own section, and a Previous Locks table lists replaced and cancelled locks with their notes.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Lock Queue",
        feature: "Expanded rows",
        title: "Lock notes in the Lock Queue",
        description:
          "Expanding a row or opening more details now shows the lock notes.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Lock Queue",
        feature: "Pre-Close",
        title: "Lock Queue filters",
        description:
          "Pre-Close has Loan Stage and Closing Date columns, filters under every column, and an Include Dead / Inactive / Closed Loans switch that is off by default.",
      },
      {
        type: "feature",
        section: "Closing",
        area: "Closing readiness",
        feature: "Scheduling entries",
        title: "Edit scheduling entries from closing readiness",
        description:
          "On the closing readiness card, click a scheduling entry to edit its value right there or jump to the place on the loan where it is edited.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Summary",
        feature: "Stage tracker",
        title: "Acquisition stages and on hold",
        description:
          "Acquisitions gain the Condition Clearance and Final Review stages and can be put on hold from the Summary tab, then resumed at the stage they were held at. The stage tracker lists the steps for the current stage.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "Settlement dates",
        title: "Settlement dates on acquisitions",
        description:
          "Acquisitions show an editable Expected Settlement date in the header and on Loan Info, ask for a Funded Date once the loan is closed, and show the Salesforce stage.",
      },
      {
        type: "feature",
        section: "Pipelines & Queues",
        area: "Acquisitions",
        feature: "Columns",
        title: "New columns on the Acquisitions Pipeline",
        description:
          "Stage Steps, Loan Closed? and Settlement Target columns, and the expected settlement and closing dates can be edited in the cell.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Views",
        title: "Links to each Documents view",
        description:
          "Each view on the Documents tab has its own link that can be bookmarked or shared.",
      },
      {
        type: "feature",
        section: "Loan Drilldown",
        area: "Loan Info",
        feature: "New options",
        title: "Delayed Purchase and Borrower Classification options",
        description:
          "Delayed Purchase is a new loan purpose, and Borrower Classification is a new exception category.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Manage Lock",
        title: "Manage Lock open to all users",
        description:
          "Anyone who can see the Loan Lock tab can now extend or cancel a lock. An extension is still a request that capital markets confirms.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Properties & Applicants",
        feature: "Assigned documents",
        title: "Assigned documents on properties and applicants",
        description:
          "The documents panel always has an upload area, separate expand and collapse buttons, and scrolls when the list is long. The property sidebar's Documents tab uses the same panel.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Documents",
        feature: "Document Group Coverage",
        title: "Document Group Coverage on the Documents tab",
        description:
          "The scope is picked from a row of pills, and switches hide empty groups or missing types.",
      },
      {
        type: "improvement",
        section: "Closing",
        area: "Closing readiness",
        feature: "Adjust dates",
        title: "Adjust dates on closing readiness",
        description:
          "Closing status and wire status are dropdowns, and the Closer list shows only the Closers team.",
      },
      {
        type: "improvement",
        section: "General",
        area: "Forms",
        feature: "Quick edit",
        title: "Quick edit for single fields",
        description:
          "Editing one field, such as Expected Closing in the loan header or a cell in the pipeline, opens a small window titled with that field.",
      },
      {
        type: "improvement",
        section: "Loan Drilldown",
        area: "Conditions & Exceptions",
        feature: "Suggested findings",
        title: "Suggested findings banner on Conditions and Exceptions",
        description:
          "The banner no longer shows when there are no open suggested conditions or exceptions.",
      },
      {
        type: "improvement",
        section: "General",
        area: "App-wide",
        feature: "General UI improvements",
        title: "General UI improvements",
        description:
          "Updated Lock Queue and Appraisal Queue toolbars, toggle switches, sidebar tabs and pinned workspace tabs.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Property address",
        title: "Manually entered property address",
        description:
          "Fixed an issue where a manually entered property address was changed on save, so the address you enter is the one that shows.",
      },
      {
        type: "fix",
        section: "General",
        area: "Forms",
        feature: "Date fields",
        title: "Date fields jumping to 1900",
        description:
          "Fixed date fields, including the Confirm Lock form, saving a 1900s year while the year was still being typed.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Countdown",
        title: "Lock days remaining",
        description:
          "Fixed the lock countdown showing one day too many.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Loan Lock",
        feature: "Confirm Lock",
        title: "Notes when confirming a lock",
        description:
          "Fixed a note added when confirming a lock replacing the requester's notes.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Properties",
        feature: "Original purchase price",
        title: "Original purchase price on refinances",
        description:
          "Fixed the original purchase price and date not saving from Add Property and Quick Intake, and showing blank on the Summary tab.",
      },
      {
        type: "fix",
        section: "Loan Drilldown",
        area: "Header",
        feature: "Update loan windows",
        title: "Update loan windows",
        description:
          "Fixed update loan windows staying open after a successful save.",
      },
      {
        type: "fix",
        section: "General",
        area: "Workspace tabs",
        feature: "Pinned tabs",
        title: "Unpinning workspace tabs",
        description:
          "Fixed unpinning a tab sometimes taking two clicks.",
      },
      {
        type: "fix",
        section: "Pipelines & Queues",
        area: "Pipeline boards",
        feature: "Saved layouts",
        title: "Saved pipeline layouts",
        description:
          "Fixed new columns landing at the far right of a saved layout.",
      },
    ],
  },
  {
    date: "2026-09-29T18:00:00",
    version: "2026.09.29",
    title: "Pipeline columns, applicant documents, correspondent status",
    summary:
      "New pipeline columns, a reworked documents section on the applicant card, correspondent status with a reason, and wider access to the Loan Lock tab.",
    changes: [
      {
        type: "feature",
        title: "Status Start Date column on the Loan Pipeline",
        description:
          "The pipeline table has a Status Start Date column.",
      },
      {
        type: "feature",
        title: "Loan history on the applicant card",
        description:
          "A collapsible section on the applicant card holds the loan history found for that applicant.",
      },
      {
        type: "feature",
        title: "Documents on the applicant card",
        description:
          "The documents section of an applicant card can toggle between what is missing and what is uploaded and assigned to that applicant, and can collapse or expand every document type at once. Hover a document type to upload a new file, which is assigned to the applicant, or to assign one already on the loan, and expanding the type gives the same through Add New.",
      },
      {
        type: "feature",
        title: "Experience records on the applicant card",
        description:
          "An applicant with experience records entered on the Experience Calc. tab shows them in a collapsible section on their card.",
      },
      {
        type: "feature",
        title: "Correspondent status",
        description:
          "A correspondent's status now has its own column in the table and shows wherever the correspondent appears. Setting it to Inactive asks for a reason, which is shown under the name in the header.",
      },
      {
        type: "feature",
        title: "Distributed on Origination Date on a draw",
        description:
          "The Net Draw form has a Distributed on Origination Date checkbox.",
      },
      {
        type: "feature",
        title: "Admin tab on the loan",
        description:
          "A new Admin tab shows the permissions behind the gated areas of the loan, from the roles allowed down to the specific users.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        title: "Exceptions column on the Loan Pipeline",
        description:
          "The Exceptions cell shows the count of exceptions, and hovering it gives more detail on them.",
      },
      {
        type: "improvement",
        title: "Loan Lock tab open to more roles",
        description:
          "Sales and loan officers, processing and closers can now see the Loan Lock tab, which was limited to capital markets.",
      },
      {
        type: "improvement",
        title: "Missing documents on the applicant card",
        description:
          "A missing item is marked with a background highlight in place of the Verifications badge, which was removed.",
      },
      {
        type: "fix",
        title: "Typing in the correspondent sidebar",
        description:
          "Fixed an issue with typing in the correspondent sidebar.",
      },
    ],
  },
  {
    date: "2026-09-25T18:00:00",
    version: "2026.09.25",
    title: "Quick Add menu, closing sidebar on the loan, and form fixes",
    summary:
      "A shortcut menu for common actions, closing readiness from the loan itself, a Status Start Date column, and fixes to the property, entity and sizer forms.",
    changes: [
      {
        type: "feature",
        title: "Quick Add menu",
        description:
          "Hover the Quick Add button on the right of a loan for a short menu of common actions, such as uploading a document, adding an applicant or property, or posting a comment.",
      },
      {
        type: "feature",
        title: "Closing sidebar on the loan",
        description:
          "Open the same closing readiness panel the Closing Calendar shows without leaving the loan, from the button on the right of the loan.",
        roles: ["admin", "closer"],
      },
      {
        type: "feature",
        title: "Status Start Date column",
        description:
          "The Loan Pipeline and Acquisitions pipeline show when each loan entered its current status.",
      },
      {
        type: "improvement",
        title: "Automatic checks on parsed documents",
        description:
          "The viewer flags expired dates, totals that don't add up, and names that don't match the borrower, and Show in text jumps to where a value appears in the document.",
      },
      {
        type: "improvement",
        title: "Experience Calc. on every loan",
        description:
          "The Experience Calc. tab now shows on every loan, whatever the product type.",
      },
      {
        type: "fix",
        title: "Editing a property made a copy",
        description:
          "Saving changes to an existing property no longer creates a second copy of it.",
      },
      {
        type: "fix",
        title: "Updating an entity",
        description: "Saving changes to an entity now works.",
      },
      {
        type: "fix",
        title: "Only the first sizer file was kept",
        description:
          "Add Sizer now takes one file at a time, since only the first of several was being saved.",
      },
      {
        type: "fix",
        title: "Scope of Work on loans with no budget",
        description:
          "On Budget & Draws, the Scope of Work section keeps its heading and shows the prompt to build a budget instead of disappearing.",
      },
      {
        type: "fix",
        title: "Closing status while loading",
        description:
          "The closing panel shows Checking while readiness loads, instead of a status that might be wrong.",
      },
    ],
  },
  {
    date: "2026-09-23T18:00:00",
    version: "2026.09.23",
    title: "Document Validity sub tab and expiring documents on the closing sidebar",
    changes: [
      {
        type: "feature",
        title: "Document Validity sub tab on Documents",
        description:
          "A new sub tab on the Documents tab showing how long each dated document stays good for and when it runs out.",
      },
      {
        type: "feature",
        title: "Expiring documents on the closing sidebar",
        description:
          "The loan closing sidebar now lists the documents that are expired or expiring, so you can see what has to be replaced before closing.",
      },
      {
        type: "improvement",
        title: "Entity documents on the Entity tab",
        description:
          "Updated how entity documents are displayed on the Entity tab.",
      },
      {
        type: "improvement",
        title: "Documents sub tabs",
        description:
          "General UI improvements to the Upload, Missing Documents and Document Coverage sub tabs.",
      },
    ],
  },
  {
    date: "2026-09-22T18:00:00",
    version: "2026.09.22",
    title: "General contractors on Contacts, experience points, feasibility and inspections on Budget & Draws",
    summary:
      "General contractors get their own area with a document checklist, experience is measured in qualifying points, and Budget & Draws gains feasibility and inspection sections.",
    changes: [
      {
        type: "feature",
        title: "General contractors on the Contacts tab",
        description:
          "General contractors now have a dedicated area for adding and managing them, with the general contractor document checklist beside it so those documents can be uploaded and previewed in place.",
      },
      {
        type: "feature",
        title: "Credited experience on a general contractor",
        description:
          "The general contractors list shows the credited events added on the Experience Calc. tab. Click a row to open that track record item, or add an experience item straight from the contractor.",
      },
      {
        type: "feature",
        title: "Quick access dropdown on the Contacts tab",
        description:
          "The Contacts tab has a dropdown for going straight to a specific contact type. Clicking the tab itself still opens the contacts page as before, and Add contact opens the Add Contact sidebar without taking you off the page.",
      },
      {
        type: "feature",
        title: "General contractors in the Credits section",
        description:
          "Adding a record on the Experience Calc. tab now offers general contractors in the Credits section.",
      },
      {
        type: "feature",
        title: "Qualifying category on an experience record",
        description:
          "Adding an experience record on the Experience Calc. tab now includes a Qualifying category field, which sets how much the item counts toward experience.",
      },
      {
        type: "feature",
        title: "Experience counted in qualifying points",
        description:
          "An experience item now carries a qualifying category that sets what it is worth. Fix and flip and ground up items count a full point, while an income producing purchase and general contractor work for a third party count half. The Experience Calc. tab totals qualifying points for guarantors and general contractors, and the applicant card reads in points rather than deals.",
      },
      {
        type: "feature",
        title: "Feasibility Report section on Budget & Draws",
        description:
          "A Feasibility Report section with a feasibility order status you can set per property, either in the section or from the property dropdown.",
      },
      {
        type: "feature",
        title: "Draw inspection reports on Budget & Draws",
        description:
          "Each draw shows its inspection report photos, what the inspector says every requested line is complete to, and a call out listing the requested lines no photo evidences.",
      },
      {
        type: "feature",
        title: "Section dropdown on Budget & Draws",
        description:
          "A dropdown on the tab jumps you to the section you want.",
      },
      {
        type: "feature",
        title: "Lock type on a rate lock",
        description:
          "Requesting or confirming a lock now asks for the lock type, Direct, Broker, or Correspondent, in place of the correspondent checkbox. It starts from the loan's origination channel, shows on the lock panel, and has its own column on the Lock Queue.",
      },
      {
        type: "improvement",
        title: "General Contractor Docs document group",
        description:
          "Contractor paperwork, including the license, insurance, W9 and profile form, moved out of Construction Documents into a new General Contractor Docs group. Contractor licenses and insurance default to a one year expiration, which can be changed per file.",
      },
      {
        type: "improvement",
        title: "Decimals in Verified Experience Total",
        description:
          "Verified Experience Total on the applicant sidebar accepts decimals, so half point items can be entered.",
      },
      {
        type: "improvement",
        title: "Key Roles and the Create Contact form",
        description:
          "Improvements to the Key Roles section on the Contacts tab and to the Create Contact form.",
      },
      {
        type: "improvement",
        title: "90 day option removed from locks",
        description:
          "The 90 day term is gone from every place a lock is confirmed or extended.",
      },
      {
        type: "improvement",
        title: "Funding Shield borrower type",
        description:
          "Borrower Type on Funding Shield now defaults to Entity instead of Individual.",
      },
      {
        type: "improvement",
        title: "Confirmation dialogs",
        description:
          "The confirmation dialogs across the loan now match the rest of the drilldown forms.",
      },
      {
        type: "fix",
        title: "Editing overlays on a correspondent",
        description:
          "Fixed an issue preventing overlays from being edited on the Overlays tab of a correspondent.",
      },
      {
        type: "fix",
        title: "Phone numbers that showed as blank",
        description:
          "A phone number saved as a number rather than text displayed as blank on the record. It now shows.",
      },
    ],
  },
  {
    date: "2026-09-18T18:00:00",
    version: "2026.09.18",
    title: "Loan Closed? on acquisition create, Loan Closer column, verified experience",
    summary:
      "Closing details captured when an acquisition loan is created, a Loan Closer column on the acquisition pipeline, and fixes to user timelines and the Experience section.",
    changes: [
      {
        type: "feature",
        title: "Loan Closed? on the Create Acquisition Loan screen",
        description:
          "Creating an acquisition loan now asks whether the loan is closed, with a required date that depends on the answer. Yes asks for the Origination / Note Date, No asks for the Estimated Closing Date.",
      },
      {
        type: "feature",
        title: "Loan Closer column on the Loan Acquisition Pipeline",
        description:
          "The acquisition pipeline table now has a Loan Closer column.",
      },
      {
        type: "improvement",
        title: "User Activity timelines",
        description:
          "Improvements to the accuracy of the user timelines on the User Activity screen.",
      },
      {
        type: "improvement",
        title: "Experience section on the Applicants tab",
        description:
          "Minor display changes to the Experience section on the applicant card.",
      },
      {
        type: "fix",
        title: "Verified Experience on the Applicants tab",
        description:
          "Verified Experience in the Experience section of the applicant card now works.",
      },
    ],
  },
  {
    date: "2026-09-17T18:00:00",
    version: "2026.09.17",
    title: "Experience Calc. tab, DocGroup Coverage sub tab, applicant fixes",
    summary:
      "A new tab for applicant track record items, a visual of document coverage on the Documents tab, and fixes on the Applicants tab.",
    changes: [
      {
        type: "feature",
        title: "Experience Calc. tab on the loan",
        description:
          "A new tab for managing applicant track record items. It appears on the loans where experience applies.",
      },
      {
        type: "feature",
        title: "DocGroup Coverage sub tab on Documents",
        description:
          "A quick visual of every document group and document type, showing which documents are uploaded and assigned to each.",
      },
      {
        type: "feature",
        title: "Key loan details and custom fields on the Closing Calendar sidebar",
        description:
          "The loan details sidebar on the Closing Calendar now shows key loan information, and you can add the loan fields that matter to you. Your added fields are saved to you and stay until you remove them.",
        links: [{ label: "Open the calendar", url: "/loans/closing/calendar" }],
      },
      {
        type: "improvement",
        title: "Primary Applicant first on the Applicants tab",
        description:
          "The Primary Applicant card is now always the first card in the list of applicants.",
      },
      {
        type: "improvement",
        title: "Ocrolus documents must be PDF",
        description:
          "Documents sent to Ocrolus are now checked up front and only PDF files are accepted.",
      },
      {
        type: "fix",
        title: "Borrower addresses not showing",
        description:
          "Fixed an issue where borrower addresses were not displayed.",
      },
    ],
  },
  {
    date: "2026-09-16T18:00:00",
    version: "2026.09.16",
    title: "Closing tab, acquisition loans in search and calendar, applicant card updates",
    summary:
      "A new Closing tab on every loan, acquisition loans in the header search and Closing Calendar, and faster document work on the Applicants and Properties tabs.",
    changes: [
      {
        type: "feature",
        title: "Closing tab on the loan",
        description:
          "Every loan now has a Closing tab with the same readiness view and actions as the Closing Calendar, plus a sub tab for uploading the closing document checklist.",
      },
      {
        type: "feature",
        title: "Closing Scheduled Date & Time in Loan Information",
        description:
          "The scheduled closing date and time is now an editable field in Loan Information on the loan drilldown.",
      },
      {
        type: "feature",
        title: "Credit and background docs on the applicant card",
        description:
          "Assign Credit Auth, Credit Report, and Background Check documents and dates directly on the applicant card on the Applicants tab. Updating a date there is the same as updating it in the sidebar.",
      },
      {
        type: "improvement",
        title: "Acquisition loans in the header loan search",
        description:
          "The loan search in the header now returns acquisition loans alongside the rest of the pipeline.",
      },
      {
        type: "improvement",
        title: "Closing Calendar",
        description:
          "General UI and functionality improvements throughout the calendar, and acquisition loans are now included.",
        links: [{ label: "Open the calendar", url: "/loans/closing/calendar" }],
      },
      {
        type: "improvement",
        title: "Experience and Track Record document group",
        description:
          "Some document types moved out of Borrower Documents into a new Experience and Track Record group on the Documents tab.",
      },
      {
        type: "improvement",
        title: "Borrower Verified Experience for everyone",
        description:
          "Borrower Verified Experience on the Applicants tab is now visible and editable to all users.",
      },
      {
        type: "improvement",
        title: "Quicker uploads on the Applicants and Properties tabs",
        description:
          "The documents area in the right column of each applicant and property is faster to use for uploading files and assigning existing documents.",
      },
      {
        type: "improvement",
        title: "DSCR tab",
        description:
          "Progress on the DSCR tab updates. Improvements and changes are still in progress.",
      },
      {
        type: "fix",
        title: "Loan app pre population on Create Loan",
        description:
          "Fixed an issue where the loan application did not pre populate on the create loan screen.",
      },
      {
        type: "fix",
        title: "Salesforce contact search on the loan",
        description:
          "Fixed an issue where certain contacts were left out of the Salesforce contact search results.",
      },
      {
        type: "fix",
        title: "Primary Applicant badge on the applicant card",
        description:
          "Fixed an issue with how the Primary Applicant badge displayed on the applicant card on the Applicants tab.",
      },
    ],
  },
  {
    date: "2026-09-10T18:00:00",
    version: "2026.09.10",
    title: "Order counts on the loan Orders tab",
    changes: [
      {
        type: "fix",
        title: "Order counts on the Orders tab",
        description:
          "The summary counts across the top of the tab stayed at zero even when the loan had appraisal reviews on it. They now count every review placed on the loan and break it out by where each one stands.",
      },
    ],
  },
  {
    date: "2026-09-09T18:00:00",
    version: "2026.09.09",
    title: "Document insights, conditional field reveal & copy conditions by status",
    summary:
      "A reworked Documents tab with filterable overview widgets, control over which conditions get copied, and document expirations that stop at closing.",
    changes: [
      {
        type: "feature",
        title: "Document overview widgets",
        description:
          "Cards above the documents table summarize review progress, shown versus hidden, expirations, and what the documents are linked to. Click one to filter the table to it.",
      },
      {
        type: "feature",
        title: "Choose which conditions get copied",
        description:
          "Copy conditions by status, picking the statuses to include, or copy just the rows currently displayed. Open and In Progress are selected by default.",
      },
      {
        type: "feature",
        title: "Show conditionally hidden fields",
        description:
          "Loan Information can reveal the fields a loan's product or purpose normally hides. Hover a revealed field to read the condition that has to be met for it to show.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        title: "Documents tab layout",
        description:
          "The sub tab switcher moved to the top of the page, the information above the table is more compact, and the charts of the displayed data were reworked.",
      },
      {
        type: "improvement",
        title: "Duplicate document names are refused",
        description:
          "Renaming a document to a name another file on the loan already uses is now blocked, in both the inline editor and Edit Details, rather than appearing to save and reverting.",
      },
      {
        type: "improvement",
        title: "Rehab fields on Multifamily Value Add",
        description:
          "Multifamily Value Add now shows the rehab and holdback fields, the same as the construction products.",
      },
      {
        type: "improvement",
        title: "Wire tie-out balances to the penny",
        description:
          "The balance checks on Wire & Funding no longer allow a dollar of drift. The HUD tie-out and the net wire agreement pass only when the figures match exactly.",
      },
      {
        type: "fix",
        title: "Renaming a document",
        description: "Fixed an error when renaming a document.",
      },
      {
        type: "fix",
        title: "Saves that looked like they reverted",
        description:
          "A save on a loan could be overwritten a moment later by a refresh that was already loading, so the change appeared to undo itself. This affected every tab, not just documents.",
      },
      {
        type: "fix",
        title: "Document expirations on closed loans",
        description:
          "On a closed loan, document validity is now measured against the closing date instead of today, so documents that were valid at close no longer all read as expired. The tab says which day it is measuring against.",
      },
      {
        type: "fix",
        title: "Tooltips on pinned tabs",
        description:
          "The Unpin tab tooltip no longer sticks to the screen after dragging or scrolling the workspace tab strip.",
      },
      {
        type: "fix",
        title: "Need flood cert count on Properties",
        description:
          "The Need flood cert card on the Properties tab now accounts for a manually entered Flood Cert Date, so a property with a cert already on file no longer counts as needing one.",
      },
    ],
  },
  {
    date: "2026-09-04T18:00:00",
    version: "2026.09.04",
    title: "GUC Sizer for admins, entity member import & exception comments",
    summary:
      "A GUC Sizer on the admin screens, faster ways to build out an entity, and exception comments you can read without opening each one.",
    changes: [
      {
        type: "feature",
        title: "Admin GUC Sizer",
        description: "A GUC Sizer was added to the admin screens.",
        roles: ["admin"],
      },
      {
        type: "feature",
        title: "Expected closing date in the loan header",
        description:
          "Change the expected closing date straight from the loan header.",
      },
      {
        type: "feature",
        title: "Import borrower data onto an entity member",
        description:
          "Pull an existing borrower's details in when adding a member, instead of retyping them.",
      },
      {
        type: "feature",
        title: "Quick add for nested entities and members",
        description:
          "Add a nested entity or member in one step from the entity chart.",
      },
      {
        type: "feature",
        title: "Comments column on Exceptions",
        description:
          "The exceptions table shows the most recent comment, and hovering it opens the full thread.",
      },
      {
        type: "feature",
        title: "AI suggestions bar on Exceptions",
        description:
          "Exceptions the AI review staged from the loan's documents now appear above the table.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        title: "Entity tab display",
        description:
          "Clearer information and legend on the entity chart, plus general UI improvements throughout the tab.",
      },
      {
        type: "improvement",
        title: "Exceptions table cells",
        description:
          "General UI improvements to the data shown in the exceptions table.",
      },
      {
        type: "improvement",
        title: "Comments in the Edit Exception sidebar",
        description:
          "Exception comments are easier to read, with a toggle to see all of the loan's comments alongside them.",
      },
    ],
  },
  {
    date: "2026-09-03T18:00:00",
    version: "2026.09.03",
    title: "Ocrolus on Documents, inline fixes when closing a loan & Entity tidy-up",
    summary:
      "A new Ocrolus sub-tab on Documents, missing data fixed without leaving the move-to-funding modal, and a cleaner Entity tab.",
    changes: [
      {
        type: "feature",
        title: "Ocrolus sub-tab on Documents",
        description:
          "The Documents tab has an Ocrolus sub-tab for pre underwriting and acquisition loans.",
      },
      {
        type: "feature",
        title: "Edit the entity address",
        description:
          "The entity address can now be edited from the Entity tab.",
      },
      {
        type: "improvement",
        title: "Fill in missing data without leaving the modal",
        description:
          "The Mark Loan Closed and move to funding modal now lets you edit the missing fields inline, so you can finish in one place.",
      },
      {
        type: "improvement",
        title: "Delete from the Entity edit sidebar",
        description:
          "The Entity edit sidebar header now has a delete button, matching the other sidebars.",
      },
      {
        type: "improvement",
        title: "Entity spacing",
        description: "More room in the Entity tab so longer names fit.",
      },
      {
        type: "fix",
        title: "Moving a loan to funding",
        description:
          "Missing data no longer blocks a loan from being moved to funding.",
      },
      {
        type: "fix",
        title: "Wire & Funding on acquisition loans",
        description:
          "Opening the Wire & Funding tab on an acquisition loan no longer throws an error.",
      },
      {
        type: "fix",
        title: "Dates in the loan changelog",
        description:
          "Fixed the dates and times shown in the loan changelog, which could read a few hours off or land on the wrong day.",
      },
    ],
  },
  {
    date: "2026-08-28T18:00:00",
    version: "2026.08.28",
    title: "Sizer loan creation fields, document group selection & activity timeline detail",
    summary:
      "More fields when creating a loan from a sizer, select a whole group of documents at once, underwriting notes locked to underwriting, and a more detailed, date-correct User Activity timeline.",
    changes: [
      {
        type: "feature",
        title: "More fields when creating a loan from a sizer",
        description:
          "Pick the correspondent, loan purpose and loan product while creating the loan, instead of setting them afterward.",
      },
      {
        type: "feature",
        title: "Select a whole document group",
        description:
          "Group rows in Loan Documents now have a checkbox that selects every row in that group.",
      },
      {
        type: "improvement",
        title: "Underwriting notes are underwriting only",
        description:
          "Only admins and users in an underwriting group can edit the underwriting note. Everyone else can still read it, and the processing note is unchanged.",
      },
      {
        type: "improvement",
        title: "Session edit breakdown and loans touched",
        description:
          "Hover a session's edit count to see it split into data, conditions, comments, exceptions and creations. The loans touched in the session are now listed and open in a new tab when clicked.",
        links: [{ label: "Open User Activity", url: "/users/activity" }],
        roles: ["admin"],
      },
      {
        type: "improvement",
        title: "Expand a session for full detail",
        description:
          "Each session on the timeline expands to show exactly what was updated.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        title: "Dates on the Today and Yesterday chips",
        description:
          "The Today and Yesterday chips now show their date beside the label.",
        roles: ["admin"],
      },
      {
        type: "fix",
        title: "User Activity dates",
        description:
          "Fixed a day chip that could read as a future date and evening work landing on the wrong day, so a 9 PM session no longer shows on the next day's timeline. The rollup buckets by UTC day while the clock is Eastern.",
        roles: ["admin"],
      },
    ],
  },
  {
    date: "2026-08-27T18:00:00",
    version: "2026.08.27",
    title: "Wire & Funding overhaul, Correspondent Box folders & notification preferences",
    summary:
      "A rebuilt Funding Shield experience on the Wire & Funding tab, Box folders on the Correspondent profile, and notification preferences for every alert the system sends.",
    changes: [
      {
        type: "feature",
        title: "Box folders on the Correspondent profile",
        description:
          "The Correspondent drilldown's Profile tab now has a Box folders section.",
      },
      {
        type: "feature",
        title: "Funding Entity guard on Wire & Funding",
        description:
          "If the loan has no Funding Entity set, the Wire & Funding tab now explains that instead of showing incomplete content, and takes you to Loan Terms to set it.",
      },
      {
        type: "feature",
        title: "Edit Funding Shield status and date requested",
        description:
          "Set the Funding Shield status and Date Requested right on the Wire & Funding tab, so you no longer have to go back to Loan Information.",
      },
      {
        type: "feature",
        title: "Previously uploaded Funding Shields",
        description:
          "The tab now lists the Funding Shields already uploaded to the loan's documents.",
      },
      {
        type: "feature",
        title: "New funding sub tabs",
        description:
          "Several sub tabs were added for viewing funding related information in one place.",
      },
      {
        type: "feature",
        title: "Funding Shield shortcut on Loan Terms",
        description:
          "Loan Terms now links straight to where you update the Funding Shield.",
      },
      {
        type: "improvement",
        title: "Funding Shield tab overhaul",
        description:
          "The Funding Shield tab was rebuilt with side by side editing and preview, so you can see the document update as you change the data.",
      },
      {
        type: "improvement",
        title: "Funding Shield PDF design",
        description:
          "Style and layout improvements to the generated Funding Shield PDF.",
      },
      {
        type: "improvement",
        title: "Wire Instructions tab",
        description: "Major UI improvements to the Wire Instructions tab.",
      },
      {
        type: "improvement",
        title: "Save banner on Loan Information",
        description:
          "An improved save banner that also lets you save with Ctrl + S.",
      },
      {
        type: "improvement",
        title: "Loan Terms display",
        description:
          "Small quality of life improvements to how some dropdowns and values are displayed.",
      },
      {
        type: "improvement",
        title: "Notification preferences for every alert",
        description:
          "Preferences now cover every alert the system sends, including document accepted, rejected and status changes, condition and exception comments, and loan role assignments, so you can control each one instead of taking the default.",
      },
      {
        type: "improvement",
        title: "RTL & GUC pricing warnings",
        description:
          "The pricing screen now shows rate units and validation warning banners when a sheet cannot be saved.",
        roles: ["admin"],
      },
      {
        type: "improvement",
        title: "Pricing preview rail precision",
        description: "The preview rail now shows rates to 3 decimal places.",
        roles: ["admin"],
      },
      {
        type: "fix",
        title: "Funding Shield PDF data",
        description:
          "Fixed cases where the generated PDF could be populated with inaccurate data.",
      },
      {
        type: "fix",
        title: "RTL & GUC pricing display and data",
        description:
          "Fixed display and data issues on the RTL and GUC admin pricing screens.",
        roles: ["admin"],
      },
    ],
  },
  {
    date: "2026-08-13T18:00:00",
    version: "2026.08.13",
    title: "Saved pipeline views & document shortcuts",
    summary:
      "Update a saved pipeline view in place, open documents in their own tab, plus a fix for saved layouts.",
    changes: [
      {
        type: "feature",
        title: "Update a saved pipeline view",
        description:
          "Change the columns, filters, or sort on a saved view and save over it, so you no longer have to delete it and start again.",
      },
      {
        type: "improvement",
        title: "Open documents in a new tab",
        description:
          "The documents table now has an Open in New Tab action, so you can keep a file open beside the loan.",
      },
      {
        type: "fix",
        title: "Saving pipeline layouts",
        description:
          "Saved pipeline layouts now stick to your profile, so your views come back the next time you open the pipeline.",
      },
    ],
  },
  {
    date: "2026-07-30T18:00:00",
    version: "2026.07.30",
    title: "Budget & Draws, quick uploads & loan header controls",
    summary:
      "A new Budget & Draws tab and draw requests, a reusable upload window, quick uploads from Quick Add, and new loan header controls.",
    changes: [
      {
        type: "feature",
        title: "Budget & Draws tab",
        description:
          "A new Budget & Draws tab on the loan for construction budgets and draws. This is an early work in progress.",
      },
      {
        type: "feature",
        title: "Submit draw requests",
        description:
          "Submit a draw request on a loan from the Budget & Draws tab.",
      },
      {
        type: "feature",
        title: "Reusable document upload",
        description:
          "A new document upload window that works the same way across different parts of the portal.",
      },
      {
        type: "feature",
        title: "Quick upload from Quick Add",
        description:
          "The loan's Quick Add button can now quick-upload documents to the loan, a borrower, or a property, and upload sizer files.",
      },
      {
        type: "feature",
        title: "Loan status on the loan header",
        description:
          "Change a loan's status right from a dropdown in the loan header.",
      },
      {
        type: "feature",
        title: "Expected closing date on the loan header",
        description:
          "The loan header now shows the loan's expected closing date.",
      },
      {
        type: "feature",
        title: "Loan Information redesign (early preview)",
        description:
          "Started a Loan Information redesign that lets you update loan info, borrowers, and properties from a single tab. Scroll to the bottom of the Loan Info tab and click the button to preview it.",
      },
      {
        type: "improvement",
        title: "Condition process polish",
        description:
          "General UI improvements to the condition process.",
      },
      {
        type: "improvement",
        title: "Better entity document uploads",
        description:
          "Improved the experience for uploading entity documents on the Entity tab.",
      },
      {
        type: "fix",
        title: "Correct entity member count",
        description:
          "Fixed the Entity Members count on the Entity tab, which was showing the wrong number.",
      },
      {
        type: "fix",
        title: "Notifications stay connected to live updates",
        description:
          "Fixed an issue where the notification stream could disconnect from live updates and stop reconnecting.",
      },
    ],
  },
  {
    date: "2026-07-24T18:00:00",
    version: "2026.07.24",
    title: "Reorder pinned tabs",
    summary: "Drag your pinned loan tabs to reorder them.",
    changes: [
      {
        type: "feature",
        title: "Drag to reorder pinned tabs",
        description:
          "Drag a pinned loan tab to reorder it, and the order is remembered.",
      },
    ],
  },
  {
    date: "2026-07-21T18:00:00",
    version: "2026.07.21",
    title: "AI reviews, conditions status filter & summary polish",
    summary:
      "On-demand AI reviews for authorized users, a status filter on the conditions checklist, and a cleaner expanded Summary view.",
    changes: [
      {
        type: "feature",
        title: "Added on-demand AI review functionality",
        description:
          "Authorized users can run on-demand AI reviews on a loan and see their status.",
        roles: ["admin"],
      },
      {
        type: "feature",
        title: "Added ability to view documents from the sidebar",
        description:
          "A new button in the loan drilldown sidebar quick-opens the loan's documents.",
      },
      {
        type: "feature",
        title: "Added ability to filter conditions by status in checklist view",
        description:
          "Filter the loan conditions checklist by status to focus on what you need.",
      },
      {
        type: "feature",
        title: "Added ownership percentage on the Entity tab",
        description:
          "The entity ownership chart now shows each entity's ownership percentage.",
      },
      {
        type: "improvement",
        title: "Improved expanded summary details",
        description:
          "Improved the look of the expanded details in the loan drilldown Summary tab.",
      },
      {
        type: "improvement",
        title: "Parsed document filtering and grouping",
        description:
          "Improved filtering and grouping of parsed documents in the loan Documents tab, with search and group-by status, category, or type.",
      },
      {
        type: "improvement",
        title: "Improved condition editing",
        description:
          "Improved the condition editing experience in the checklist view, with an inline detail panel for each condition.",
      },
    ],
  },
  {
    date: "2026-07-20T18:00:00",
    version: "2026.07.20",
    title: "Email missing documents & parsed documents for everyone",
    summary:
      "Send a loan's missing-documents list by email, and parsed documents now show for all users.",
    changes: [
      {
        type: "feature",
        title: "Email missing loan documents",
        description:
          "From a loan's missing documents, copy the list to paste into an email, or open it directly in a new email with the list already filled in.",
      },
      {
        type: "fix",
        title: "Parsed documents show for everyone",
        description:
          "Fixed parsed documents not showing for all users. They are now visible to everyone.",
      },
    ],
  },
  {
    date: "2026-07-15T18:00:00",
    version: "2026.07.15",
    title: "Loan activity timeline, cached data controls, and fixes",
    summary:
      "The loan Summary gains a stage timeline and a redesigned activity feed, your profile can manage cached data, and a few fixes landed.",
    changes: [
      {
        type: "feature",
        title: "Stage timeline and activity on the loan Summary",
        description:
          "The loan Summary has a new expandable Stage timeline and activity view. Each stage shows the exact date and time it started and finished, how long the loan spent in it, a longest stage marker, and how many of the stages are complete, next to a live feed of the most recent activity on the loan.",
      },
      {
        type: "feature",
        title: "Cached data controls in your profile",
        description:
          "Profile Settings has a new Cached data section that lists what the portal saves in your browser, grouped into reference data, pipeline views and filters, table layouts, preferences, and sign-in settings. You can view each stored value, refresh reference data from the server, delete an individual item, or clear everything at once. Clearing only removes app data and does not sign you out.",
        links: [{ label: "Open Profile", url: "/profile" }],
      },
      {
        type: "improvement",
        title: "Clearer, interactive recent activity",
        description:
          "Recent activity now reads as an avatar led feed grouped by day. Comments and conditions appear as rich cards, document uploads and updates show their category and status, and loan field changes show a simple what changed list. Documents can be opened straight from the feed, images preview in place, and when the activity panel is open on the right the file preview opens on the left so you can read both at once.",
      },
      {
        type: "fix",
        title: "Sub status no longer required to update loan status",
        description:
          "You can change a loan's status without also selecting a sub status. The sub status is now optional.",
      },
      {
        type: "fix",
        title: "Acquisitions pipeline opened loans to the wrong path",
        description:
          "Clicking a loan in the Acquisitions pipeline table now opens the correct loan drilldown instead of navigating to the wrong path.",
      },
    ],
  },
  {
    date: "2026-07-14T18:00:00",
    version: "2026.07.14",
    title: "Portfolio loans, parsed documents & condition mentions",
    summary:
      "Loans with many properties are far easier to work through, parsed documents can be viewed alongside the original file, and condition comments support @ mentions.",
    changes: [
      {
        type: "feature",
        title: "Property and guarantor chips in the loan header",
        description:
          'Properties and guarantors in the loan overview now render as clickable chips instead of one long comma separated line. Loans with more than two show the first couple plus a "+N more" chip that opens a searchable list, so the header stays one row tall even on a 15 property portfolio. Clicking any chip opens that property or guarantor\'s detail sidebar.',
      },
      {
        type: "feature",
        title: "Compact view on the Properties tab",
        description:
          "The Properties tab has a new Compact view that lays the properties out as a dense grid of small cards showing the address, key values, square footage and document count. Loans with more than six properties open in Compact by default, and your choice is remembered.",
      },
      {
        type: "feature",
        title: "View parsed documents",
        description:
          "Documents processed by extraction can be opened from the Documents tab in a three pane viewer: the original file, the parsed text, and the structured data pulled from it. The parsed text and structured fields are editable, and your edits are saved separately so the original extraction is preserved.",
      },
      {
        type: "feature",
        title: "Closed Loans pipeline view",
        description:
          "A Closed Loans board now lists the loans that have closed. Open it from the caret on the Closing tab, alongside Clear to Close and the calendar.",
        links: [{ label: "Open Closed Loans", url: "/loans/closed" }],
      },
      {
        type: "feature",
        title: "Acquisitions pipeline",
        description:
          "A new Acquisitions tab in the top nav lists the closed loans purchased from correspondents. Each one opens the same loan drilldown you already use, pointed at the acquisitions records.",
        links: [{ label: "Open Acquisitions", url: "/acquisitions" }],
      },
      {
        type: "improvement",
        title: "Marking a loan closed or dead from the status selector",
        description:
          "The loan status selector now lists every status stacked vertically, with Inactive, Dead and Closed grouped below a divider. Picking a status that has sub statuses (like Processing or Clear to Close) opens the sub status list beside it so you set both in one confirm. Closed and Dead now open their own confirmation step instead of the separate buttons on the Loan Info tab: Closed shows a readiness overview of the checklist, conditions and wire and funding, then asks for the closing date, and Dead asks for a reason and notes. The buttons on the Loan Info tab still work and open the same screens.",
      },
      {
        type: "fix",
        title: "@ mentions in condition comments",
        description:
          "Condition comments now use the rich comment editor, so you can @ mention a user and they get notified. Previously the box was plain text and mentions were dropped.",
      },
    ],
  },
  {
    date: "2026-07-10T18:00:00",
    version: "2026.07.10",
    title: "Missing documents tab & Documents tab polish",
    summary:
      "A Missing documents tab, an improved upload flow, and clearer validity and review-status displays.",
    changes: [
      {
        type: "feature",
        title: "Missing documents tab",
        description:
          "A new Missing tab in the Documents tab lists the documents still needed for the loan.",
      },
      {
        type: "feature",
        title: "Filter documents by status",
        description:
          "Click a review-status chip to filter the loan documents table in the loan drilldown to just that status.",
      },
      {
        type: "feature",
        title: "Filter documents by validity",
        description:
          "Click a validity item to filter the loan documents table in the loan drilldown by validity (valid, expiring soon, or expired). It combines with the status filter.",
      },
      {
        type: "improvement",
        title: "Improved file upload",
        description:
          "The file upload flow now lets you tie an upload directly to the loan, a borrower, or a property as you upload.",
      },
      {
        type: "improvement",
        title: "Clearer document validity",
        description:
          "Improved how each document's validity shows next to its filename, with clearer wording.",
      },
      {
        type: "improvement",
        title: "Clearer review status",
        description:
          "Improved the visual of a document's review status so it's easier to read at a glance.",
      },
      {
        type: "fix",
        title: "Entity name displays correctly",
        description:
          "On the loan pipeline the entity name now displays correctly.",
      },
    ],
  },
  {
    date: "2026-07-01T18:00:00",
    version: "2026.07.01",
    title: "Conditions from documents & sizer classification",
    summary:
      "Create conditions straight from a document, and classify existing sizer documents into the tracker.",
    changes: [
      {
        type: "feature",
        title: "Create a condition from a document",
        description:
          "Create a loan condition straight from a document, using the document's right-click or menu options in the Documents tab.",
      },
      {
        type: "feature",
        title: "Classify existing sizer documents",
        description:
          "Classify already-uploaded or synced Terms & Sizer documents into the sizer tracker without re-uploading, from a unified Sizer tab.",
      },
    ],
  },
  {
    date: "2026-06-29T18:00:00",
    version: "2026.06.29",
    title: "Synced document uploads in the activity log",
    summary:
      "The loan activity log now surfaces documents uploaded through integration sync.",
    changes: [
      {
        type: "improvement",
        title: "Synced uploads in the activity log",
        description:
          "The loan activity log now shows documents uploaded through integration sync, such as Box, not just in-app uploads.",
      },
    ],
  },
  {
    date: "2026-06-25T18:00:00",
    version: "2026.06.25",
    title: "Notifications screen, pop-out panels & pipeline view memory",
    summary:
      "A notifications screen, pop-out panels you can reposition and resize, remembered pipeline views, and improved loan totals.",
    changes: [
      {
        type: "feature",
        title: "Notifications screen",
        description: "View all of your notifications on a dedicated screen.",
        links: [{ label: "Open Notifications", url: "/notifications" }],
      },
      {
        type: "feature",
        title: "Remember table view",
        description:
          "The pipeline now remembers your table view. Your sort, filters, column layout, and search are restored on your next visit. On by default, and you can toggle it off from the table controls.",
      },
      {
        type: "feature",
        title: "Reposition and resize panels",
        description:
          "Pop a panel out into a floating window you can drag to reposition and resize, then dock it back when you're done.",
      },
      {
        type: "feature",
        title: "Pop out the file preview",
        description:
          "Pop the loan document preview out of the sidebar into a floating window you can reposition and resize.",
      },
      {
        type: "feature",
        title: "Pop out loan sidebars",
        description:
          "Pop any loan drilldown sidebar out into a floating window you can reposition and resize.",
      },
      {
        type: "improvement",
        title: "Loan totals and calcs",
        description:
          "Improved the loan totals and calculated fields for clearer, more accurate figures.",
      },
    ],
  },
  {
    date: "2026-06-24T18:00:00",
    version: "2026.06.24",
    title: "Loan chat, counterparties & Salesforce entity search",
    summary:
      "Chat on a loan, record its counterparty, see its external links, and pull entity details straight from Salesforce.",
    changes: [
      {
        type: "feature",
        title: "Chat on a loan",
        description:
          "Added ability to chat with your team right inside a loan, with a dedicated Chat tab and a floating chat button on the drilldown. This chat thread is independent and separate from the loan comments.",
      },

      {
        type: "feature",
        title: "Salesforce Entity Search/Import",
        description:
          "Added ability to search and import entities from Salesforce when creating a loan or adding an entity on a loan, so the entity's details fill in automatically.",
      },
      {
        type: "feature",
        title: "Salesforce Contact Search/Import",
        description:
          "Added ability to search and import contacts from Salesforce when creating a loan-related contact, so their details fill in automatically.",
      },
      {
        type: "feature",
        title: "Ability to set/view related Loan links",
        description:
          "Added ability to view/add a loan's external links, such as its Box folders. This is found on the Loan Info tab in the loan drilldown.",
      },
      {
        type: "feature",
        title: "Ability to set Loan Investor/Counterparty",
        description:
          "Added ability to set an Investor/Counterparty on a loan. This is found on the Loan Info tab in the loan drilldown.",
      },
      {
        type: "improvement",
        title: "Loan activity and changes",
        description:
          "An improved, easy-to-scan view of a loan's activity, showing who changed what and when, with search and filters.",
      },
    ],
  },
  {
    date: "2026-06-23T18:00:00",
    version: "2026.06.23",
    title: "Post-close locks, slow-request diagnostics & notification tuning",
    summary:
      "Submit post-close lock requests, send diagnostics on slow requests, a persistent document preview, and faster notifications.",
    changes: [
      {
        type: "feature",
        title: "Post-close lock requests",
        description:
          "Search Salesforce post-close loans and submit their rate lock requests right from the Lock Queue.",
        roles: ["capitalMarkets", "admin"],
      },
      {
        type: "feature",
        title: "Send diagnostics on slow requests",
        description:
          "When a request takes too long, send a support message that passes along recent console information so issues are easier to track down.",
      },
      {
        type: "improvement",
        title: "Faster notifications and sign-in checks",
        description:
          "Improved the performance and connectivity of notifications and the auth check.",
      },
      {
        type: "improvement",
        title: "Notification preferences",
        description: "Removed the Teams option from notification preferences.",
      },
      {
        type: "fix",
        title: "Document preview stays open",
        description:
          "The loan document preview no longer closes when you switch tabs. It persists, and moves to the left side of the screen when a sidebar item is opened so the two no longer overlap.",
      },
    ],
  },
  {
    date: "2026-06-18T18:00:00",
    version: "2026.06.18",
    title: "Document expiration tracking & bulk condition updates",
    summary:
      "Track document validity windows in the Documents tab, and update several condition statuses at once.",
    changes: [
      {
        type: "feature",
        title: "Document expiration tracking",
        description:
          "Track document validity windows in the Documents tab. Assigning a doc type auto-fills its expiration guideline and computes the expiration from the effective date, with inline date editing.",
      },
      {
        type: "improvement",
        title: "Bulk-update condition statuses",
        description:
          "Select multiple conditions in the checklist and update their status in one action.",
      },
    ],
  },
  {
    date: "2026-06-11T18:00:00",
    version: "2026.06.11",
    title: "Loan drilldown views, correspondent details & Salesforce sync",
    summary:
      "Switch the loan summary to a closer-focused layout, view a loan's correspondent inline, and a clearer Salesforce sync button.",
    changes: [
      {
        type: "feature",
        title: "Loan drilldown views",
        description:
          "Switch the loan summary between the default layout and a closer-focused view tailored to the closing desk. Your choice is remembered.",
      },
      {
        type: "feature",
        title: "Correspondent details from a loan",
        description:
          "Open a loan's correspondent right from the drilldown to see their profile, contacts, coverage, pricing, and more, without leaving the loan.",
      },
      {
        type: "feature",
        title: "Salesforce sync button",
        description:
          "Create a new Salesforce opportunity from a loan, or sync an already-linked loan, with separate clearly-labeled actions.",
      },
      {
        type: "feature",
        title: "View Excel files",
        description:
          "Preview Excel and other spreadsheet files right in the file viewer, no download needed.",
      },
      {
        type: "feature",
        title: "Copy conditions as email",
        description:
          "Copy a loan's outstanding conditions as a formatted, grouped list ready to paste straight into an email.",
      },
      {
        type: "feature",
        title: "Create loans from a sizer",
        description:
          "Upload a loan sizer spreadsheet to create the loan automatically, with the product auto-detected, then jump straight into it.",
      },
    ],
  },
  {
    date: "2026-06-10T18:00:00",
    version: "2026.06.10",
    title: "Appraisal queue, conditions checklist & changelog",
    summary:
      "Internal appraisal ordering, a checklist view for conditions, and this changelog.",
    changes: [
      {
        type: "feature",
        title: "Changelog",
        description:
          "Every deployment's features, improvements, and fixes in one place, filterable and searchable.",
      },
      {
        type: "feature",
        title: "Closing Calendar",
        description:
          "A calendar of upcoming closings with per-day totals, scheduled vs estimated counts, and full settlement detail on click.",
        links: [
          {
            label: "Open the Closing Calendar",
            url: "/loans/closing/calendar",
          },
        ],
      },
      {
        type: "feature",
        title: "Internal Appraisal Review orders",
        description:
          "Submit an Internal Appraisal Review order from a loan's Orders tab.",
        images: [
          {
            src: "/changelog-images/internal-appraisal-review-order.png",
            alt: "Internal Appraisal Review order form in the loan Orders tab",
            caption: "Submit the order from the loan's Orders tab.",
          },
        ],
      },
      {
        type: "feature",
        title: "Internal Appraisal Queue",
        description:
          "Work submitted appraisal reviews from the new Appraisal Queue: claim, review, then complete or cancel.",
        links: [{ label: "Open the Appraisal Queue", url: "/appraisal-queue" }],
        // Internal-staff feature: only surface it to underwriting / admin.
        roles: ["underwriter", "admin"],
      },
      {
        type: "feature",
        title: "Loan number in the browser tab",
        description:
          "Open loans show their loan number in the browser tab title.",
        images: [
          {
            src: "/changelog-images/loan-tab-titles.png",
            alt: "Browser tabs showing loan numbers in the title",
            caption: "Each open loan shows its number in the browser tab.",
          },
        ],
      },
      {
        type: "feature",
        title: "Conditions checklist",
        description:
          "The Conditions tab now has a checklist view with inline details, comments, files, and one-click status changes.",
      },

      {
        type: "fix",
        title: "Documents tab counts",
        description:
          "Document counts now exclude hidden files, so the visible status and validity totals add up.",
      },
      {
        type: "fix",
        title: "Hard exception default",
        description:
          "New exceptions no longer default to hard; they're hard only when you check that option.",
      },
      {
        type: "fix",
        title: "Interest rate precision",
        description:
          "Interest rate and other percentages now show 3 decimal places instead of 2.",
      },
    ],
  },
];

export default CHANGELOG;
